import React, { useState, useRef, useEffect } from "react";

/* ─── DESIGN TOKENS ─── */
const T = {
  blue:      "#2563EB",
  blueLight: "#EFF6FF",
  blueMid:   "#DBEAFE",
  blueDark:  "#1D4ED8",
  blueText:  "#1E40AF",
  pageBg:    "#F1F5F9",
  cardBg:    "#FFFFFF",
  border:    "#E2E8F0",
  borderMid: "#CBD5E1",
  text:      "#0F172A",
  textSub:   "#64748B",
  textHint:  "#94A3B8",
  radius:    "12px",
  radiusSm:  "8px",
  shadow:    "0 1px 3px rgba(0,0,0,.07), 0 1px 2px rgba(0,0,0,.05)",
  shadowMd:  "0 4px 12px rgba(0,0,0,.08), 0 2px 4px rgba(0,0,0,.04)",
};

/* ─── CONSTANTES ─── */
const COLORS = {
  nouveau: { bg:"#EFF6FF", text:"#1E40AF", border:"#BFDBFE", dot:"#3B82F6", label:"Nouveau" },
  encours: { bg:"#FFF7ED", text:"#9A3412", border:"#FED7AA", dot:"#F97316", label:"En cours" },
  fait:    { bg:"#F0FDF4", text:"#166534", border:"#BBF7D0", dot:"#22C55E", label:"Fait" },
  annule:  { bg:"#F8FAFC", text:"#475569", border:"#E2E8F0", dot:"#94A3B8", label:"Annulé" },
};
const LEAD_COLORS = {
  nouveau:   { bg:"#EFF6FF", text:"#1E40AF", border:"#BFDBFE", dot:"#3B82F6", label:"Nouveau" },
  interesse: { bg:"#FEFCE8", text:"#854D0E", border:"#FEF08A", dot:"#EAB308", label:"Intéressé" },
  rappel:    { bg:"#FFF7ED", text:"#9A3412", border:"#FED7AA", dot:"#F97316", label:"À rappeler" },
  gagne:     { bg:"#F0FDF4", text:"#166534", border:"#BBF7D0", dot:"#22C55E", label:"Gagné" },
  perdu:     { bg:"#F8FAFC", text:"#475569", border:"#E2E8F0", dot:"#94A3B8", label:"Perdu" },
};
const MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const DAYS_SHORT = ["L","M","M","J","V","S","D"];
const TODAY = new Date();
const D = (o=0) => new Date(TODAY.getFullYear(),TODAY.getMonth(),TODAY.getDate()+o).toISOString().split("T")[0];

/* ─── SÉCURITÉ ─────────────────────────────────────────────────────────────── */

// Hash SHA-256 (côté client — en prod utiliser bcrypt côté serveur)
async function sha256(str) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,"0")).join("");
}

// Validation mot de passe fort
function validatePassword(pwd) {
  const rules = [
    { ok: pwd.length >= 8,          msg: "Au moins 8 caractères" },
    { ok: /[A-Z]/.test(pwd),        msg: "Au moins une majuscule" },
    { ok: /[a-z]/.test(pwd),        msg: "Au moins une minuscule" },
    { ok: /[0-9]/.test(pwd),        msg: "Au moins un chiffre" },
    { ok: /[^A-Za-z0-9]/.test(pwd), msg: "Au moins un caractère spécial (!@#$...)" },
  ];
  return rules;
}
function isPasswordStrong(pwd) { return validatePassword(pwd).every(r=>r.ok); }

// Sanitize input — strip HTML/script tags
function sanitize(str) {
  return String(str||"").replace(/<[^>]*>/g,"").replace(/[<>"'`]/g,"").trim().slice(0,200);
}

// Rate limiter en mémoire (brute-force protection)
const loginAttempts = {};
function checkRateLimit(username) {
  const now = Date.now();
  const key = username.toLowerCase();
  if (!loginAttempts[key]) loginAttempts[key] = { count:0, firstAt:now, blockedUntil:0 };
  const a = loginAttempts[key];
  if (now < a.blockedUntil) {
    const secs = Math.ceil((a.blockedUntil - now) / 1000);
    return { blocked:true, msg:`Trop de tentatives. Réessayez dans ${secs}s.` };
  }
  if (now - a.firstAt > 15*60*1000) { a.count=0; a.firstAt=now; } // reset après 15min
  a.count++;
  if (a.count >= 5) { a.blockedUntil = now + 10*60*1000; return { blocked:true, msg:"Compte bloqué 10 minutes après 5 tentatives." }; }
  return { blocked:false };
}
function resetRateLimit(username) { delete loginAttempts[username.toLowerCase()]; }

// Session token simple (en prod → JWT côté serveur)
function generateToken() {
  return Array.from(crypto.getRandomValues(new Uint8Array(32))).map(b=>b.toString(16).padStart(2,"0")).join("");
}

// Stockage sécurisé session (sessionStorage — effacé à la fermeture)
const SESSION_KEY = "rw_session";
function saveSession(token, userId) { sessionStorage.setItem(SESSION_KEY, JSON.stringify({token,userId,at:Date.now()})); }
function loadSession() { try { return JSON.parse(sessionStorage.getItem(SESSION_KEY)||"null"); } catch { return null; } }
function clearSession() { sessionStorage.removeItem(SESSION_KEY); }

/* ─── API CONFIG ─── */
const API = "https://app.reparationwifi.com/backend";

async function apiFetch(endpoint, options={}) {
  const token = loadSession()?.token;
  const headers = { "Content-Type":"application/json", ...(token?{"X-Session-Token":token}:{}) };
  const res = await fetch(`${API}/${endpoint}`, { ...options, headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Erreur ${res.status}`);
  return data;
}

/* ─── APP ROOT ─── */
export default function App() {
  const [user, setUser]               = useState(null);
  const [view, setView]               = useState("login");
  const [authStep, setAuthStep]       = useState("login");
  const [pendingUser, setPendingUser] = useState(null);
  const [users, setUsers]             = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [leads, setLeads]             = useState([]);
  const [serrures, setSerrures]       = useState([]);
  const [loginForm, setLoginForm]     = useState({ lastName:"", password:"", error:"", loading:false });
  const [calMonth, setCalMonth]       = useState(TODAY.getMonth());
  const [calYear, setCalYear]         = useState(TODAY.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [modal, setModal]             = useState(null);
  const [editItem, setEditItem]       = useState(null);
  const [sendTarget, setSendTarget]   = useState(null);
  const [loading, setLoading]         = useState(false);
  const [globalError, setGlobalError] = useState("");

  const myNotifs = notifications.filter(n => n.userId===user?.id && !n.lu);
  const myInterventions = interventions.filter(i => user?.role==="technicien" ? i.technicienId===user.id : true);

  // Charger toutes les données après login
  async function loadAllData() {
    try {
      const [ints, notifs, lds, serrs, usrs] = await Promise.all([
        apiFetch("interventions.php"),
        apiFetch("notifications.php"),
        apiFetch("clients.php"),
        apiFetch("serrures.php"),
        apiFetch("utilisateurs.php"),
      ]);
      setInterventions(ints);
      setNotifications(notifs);
      setLeads(lds);
      setSerrures(serrs);
      setUsers(usrs);
    } catch(e) {
      setGlobalError("Erreur de chargement des données : " + e.message);
    }
  }

  // Restaurer session au démarrage
  useEffect(() => {
    const sess = loadSession();
    if (!sess?.token) return;
    apiFetch("utilisateurs.php")
      .then(usrs => {
        const u = usrs.find(u => u.id === sess.userId);
        if (u) {
          setUser({...u, token: sess.token});
          setView(u.role==="admin"?"dashboard":"calendrier");
          loadAllData();
        } else clearSession();
      })
      .catch(() => clearSession());
  }, []);

  async function login() {
    const lastName = sanitize(loginForm.lastName);
    const password = loginForm.password;
    if (!lastName || !password) { setLoginForm(f=>({...f,error:"Veuillez remplir tous les champs"})); return; }

    const rate = checkRateLimit(lastName);
    if (rate.blocked) { setLoginForm(f=>({...f,error:rate.msg})); return; }

    setLoginForm(f=>({...f,loading:true,error:""}));
    try {
      const data = await apiFetch("auth.php?action=login", {
        method:"POST",
        body: JSON.stringify({ lastName, password })
      });

      if (data.firstLogin) {
        setPendingUser({ id:data.userId, name:data.userName, role:data.userRole });
        setAuthStep("firstLogin");
        setLoginForm(f=>({...f,loading:false}));
        return;
      }

      resetRateLimit(lastName);
      saveSession(data.token, data.user.id);
      setUser({...data.user, token:data.token});
      setView(data.user.role==="admin"?"dashboard":"calendrier");
      setLoginForm({lastName:"",password:"",error:"",loading:false});
      await loadAllData();

    } catch(e) {
      setLoginForm(f=>({...f,loading:false,error:e.message||"Identifiant ou mot de passe incorrect"}));
    }
  }

  async function createFirstPassword(newPwd) {
    if (!pendingUser) return;
    try {
      const data = await apiFetch("auth.php?action=set_password", {
        method:"POST",
        body: JSON.stringify({ userId:pendingUser.id, password:newPwd })
      });
      saveSession(data.token, data.user.id);
      setUser({...data.user, token:data.token});
      setPendingUser(null);
      setAuthStep("login");
      setView(data.user.role==="admin"?"dashboard":"calendrier");
      await loadAllData();
    } catch(e) {
      alert("Erreur : " + e.message);
    }
  }

  async function logout() {
    try { await apiFetch("auth.php?action=logout", {method:"POST"}); } catch{}
    clearSession();
    setUser(null); setView("login"); setAuthStep("login");
    setInterventions([]); setLeads([]); setSerrures([]); setNotifications([]); setUsers([]);
    setLoginForm({lastName:"",password:"",error:"",loading:false});
  }

  async function markRead(id) {
    setNotifications(ns=>ns.map(n=>n.id===id?{...n,lu:true}:n));
    try { await apiFetch(`notifications.php?id=${id}`, {method:"PUT", body:"{}"}); } catch{}
  }
  async function markAllRead() {
    setNotifications(ns=>ns.map(n=>n.userId===user.id?{...n,lu:true}:n));
    try { await apiFetch("notifications.php", {method:"PUT", body:JSON.stringify({all:true})}); } catch{}
  }

  async function saveUser(data) {
    try {
      if (data.id) {
        await apiFetch(`utilisateurs.php?id=${data.id}`, {method:"PUT", body:JSON.stringify(data)});
      } else {
        await apiFetch("utilisateurs.php", {method:"POST", body:JSON.stringify(data)});
      }
      const usrs = await apiFetch("utilisateurs.php");
      setUsers(usrs);
      setModal(null); setEditItem(null);
    } catch(e) { alert("Erreur : " + e.message); }
  }

  async function deleteUser(id) {
    try {
      await apiFetch(`utilisateurs.php?id=${id}`, {method:"DELETE"});
      setUsers(us=>us.map(u=>u.id===id?{...u,actif:false}:u));
      setModal(null);
    } catch(e) { alert("Erreur : " + e.message); }
  }

  async function saveIntervention(data) {
    try {
      if (data.id) {
        await apiFetch(`interventions.php?id=${data.id}`, {method:"PUT", body:JSON.stringify(data)});
      } else {
        await apiFetch("interventions.php", {method:"POST", body:JSON.stringify(data)});
      }
      const [ints, notifs] = await Promise.all([
        apiFetch("interventions.php"),
        apiFetch("notifications.php"),
      ]);
      setInterventions(ints);
      setNotifications(notifs);
      setModal(null); setEditItem(null);
    } catch(e) { alert("Erreur : " + e.message); }
  }

  async function saveLead(data) {
    try {
      if (data.id) {
        await apiFetch(`clients.php?id=${data.id}`, {method:"PUT", body:JSON.stringify(data)});
      } else {
        await apiFetch("clients.php", {method:"POST", body:JSON.stringify(data)});
      }
      const [lds, notifs] = await Promise.all([
        apiFetch("clients.php"),
        apiFetch("notifications.php"),
      ]);
      setLeads(lds);
      setNotifications(notifs);
      setModal(null); setEditItem(null);
    } catch(e) { alert("Erreur : " + e.message); }
  }

  async function deleteLead(id) {
    try {
      await apiFetch(`clients.php?id=${id}`, {method:"DELETE"});
      setLeads(ls=>ls.filter(l=>l.id!==id));
      setModal(null);
    } catch(e) { alert("Erreur : " + e.message); }
  }

  async function deleteIntervention(id) {
    try {
      await apiFetch(`interventions.php?id=${id}`, {method:"DELETE"});
      setInterventions(is=>is.filter(i=>i.id!==id));
      setModal(null);
    } catch(e) { alert("Erreur : " + e.message); }
  }

  async function saveSerrure(data) {
    try {
      if (data.id) {
        await apiFetch(`serrures.php?id=${data.id}`, {method:"PUT", body:JSON.stringify(data)});
      } else {
        await apiFetch("serrures.php", {method:"POST", body:JSON.stringify(data)});
      }
      const serrs = await apiFetch("serrures.php");
      setSerrures(serrs);
      setModal(null); setEditItem(null);
    } catch(e) { alert("Erreur : " + e.message); }
  }

  async function deleteSerrure(id) {
    try {
      await apiFetch(`serrures.php?id=${id}`, {method:"DELETE"});
      setSerrures(ss=>ss.filter(s=>s.id!==id));
      setModal(null);
    } catch(e) { alert("Erreur : " + e.message); }
  }

  const getDayInterventions = (dateStr) => myInterventions.filter(i=>i.date===dateStr);
  function buildCalendar() {
    const firstDay=new Date(calYear,calMonth,1).getDay();
    const daysInMonth=new Date(calYear,calMonth+1,0).getDate();
    const startOffset=firstDay===0?6:firstDay-1;
    const days=[];
    for(let i=0;i<startOffset;i++) days.push(null);
    for(let d=1;d<=daysInMonth;d++){
      const dateStr=`${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      days.push({d,dateStr});
    }
    return days;
  }

  if (view==="login") return (
    authStep==="firstLogin"
      ? <FirstLoginPage user={pendingUser} onSubmit={createFirstPassword} onBack={()=>{setAuthStep("login");setPendingUser(null);}}/>
      : <LoginPage form={loginForm} setForm={setLoginForm} onLogin={login}/>
  );

  if (globalError) return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:T.pageBg,fontFamily:"var(--font-sans)",padding:"2rem"}}>
      <div style={{background:"#fff",borderRadius:T.radius,border:`1px solid ${T.border}`,padding:"2rem",maxWidth:420,textAlign:"center",boxShadow:T.shadowMd}}>
        <i className="ti ti-wifi-off" style={{fontSize:40,color:"#EF4444",display:"block",marginBottom:12}} aria-hidden/>
        <h2 style={{fontSize:18,fontWeight:600,color:T.text,margin:"0 0 8px"}}>Erreur de connexion</h2>
        <p style={{fontSize:13,color:T.textSub,margin:"0 0 20px",lineHeight:1.6}}>{globalError}</p>
        <button onClick={()=>{setGlobalError("");loadAllData();}} className="btn-primary" style={{width:"100%",justifyContent:"center"}}>
          <i className="ti ti-refresh" style={{fontSize:15}} aria-hidden/> Réessayer
        </button>
      </div>
    </div>
  );

  const pageContent = (
    <>
      {view==="dashboard" && user?.role==="admin" && <Dashboard interventions={interventions} users={users} leads={leads} onOpen={i=>{setEditItem(i);setModal("rdv");}}/>}
      {view==="calendrier" && <CalendarView days={buildCalendar()} calMonth={calMonth} calYear={calYear} setCalMonth={setCalMonth} setCalYear={setCalYear} getDayInterventions={getDayInterventions} selectedDate={selectedDate} setSelectedDate={setSelectedDate} user={user} users={users} onNew={date=>{setEditItem({date});setModal("rdv");}} onEdit={i=>{setEditItem(i);setModal("rdv");}}/>}
      {view==="notifications" && <NotifPage notifs={notifications.filter(n=>n.userId===user.id)} onRead={markRead} onReadAll={markAllRead} interventions={interventions} leads={leads}/>}
      {view==="liste" && <ListeView interventions={myInterventions} user={user} users={users} onEdit={i=>{setEditItem(i);setModal("rdv");}} onNew={()=>{setEditItem(null);setModal("rdv");}} onDelete={deleteIntervention}/>}
      {view==="crm" && (user?.role==="commercial"||user?.role==="admin") && <CrmView leads={leads} serrures={serrures} users={users} onNewLead={()=>{setEditItem(null);setModal("lead");}} onEditLead={l=>{setEditItem(l);setModal("lead");}} onCreateRdv={l=>{setEditItem({prefill:l});setModal("rdv");}} onSendSerrure={(serrure,lead)=>{setSendTarget({serrure,lead});setModal("sendSerrure");}} onManageSerrures={()=>{setEditItem(null);setModal("serrure");}} deleteLead={deleteLead} notifications={notifications}/>}
      {view==="utilisateurs" && user?.role==="admin" && <UtilisateursView users={users} onNew={()=>{setEditItem(null);setModal("utilisateur");}} onEdit={u=>{setEditItem(u);setModal("utilisateur");}} onDelete={deleteUser}/>}
    </>
  );

  const modals = (
    <>
      {modal==="rdv" && <InterventionModal intervention={editItem} user={user} users={users} serrures={serrures} onSave={saveIntervention} onClose={()=>{setModal(null);setEditItem(null);}} onDelete={deleteIntervention}/>}
      {modal==="lead" && <LeadModal lead={editItem} serrures={serrures} onSave={saveLead} onClose={()=>{setModal(null);setEditItem(null);}} onDelete={editItem?.id?deleteLead:null}/>}
      {modal==="serrure" && <SerrureModal serrures={serrures} onSave={saveSerrure} onDelete={deleteSerrure} onClose={()=>{setModal(null);setEditItem(null);}} onSendSerrure={(s,l)=>{setSendTarget({serrure:s,lead:l});setModal("sendSerrure");}} leads={leads}/>}
      {modal==="sendSerrure" && <SendSerrureModal serrure={sendTarget?.serrure} lead={sendTarget?.lead} leads={leads} onClose={()=>{setModal(null);setSendTarget(null);}}/>}
      {modal==="utilisateur" && <UtilisateurModal utilisateur={editItem} onSave={saveUser} onClose={()=>{setModal(null);setEditItem(null);}} onDelete={editItem?.id&&editItem.id!==user.id?deleteUser:null} currentUserId={user.id}/>}
    </>
  );

  return (
    <div style={{minHeight:"100vh",background:T.pageBg,fontFamily:"var(--font-sans)",color:T.text}}>
      <style>{`
        *{box-sizing:border-box;}
        .app-layout{display:flex;min-height:100vh;}
        .sidebar{width:240px;flex-shrink:0;background:#fff;border-right:1px solid ${T.border};display:flex;flex-direction:column;position:sticky;top:0;height:100vh;overflow:hidden;}
        .main-content{flex:1;min-width:0;padding:2rem 2.5rem;overflow-y:auto;max-height:100vh;}
        .mobile-nav{display:none;}
        .welcome-bar{display:none;}
        input,select,textarea{background:#fff;border:1.5px solid ${T.border};border-radius:${T.radiusSm};padding:9px 12px;font-size:14px;color:${T.text};outline:none;transition:border .15s;width:100%;box-sizing:border-box;}
        input:focus,select:focus,textarea:focus{border-color:${T.blue};}
        input::placeholder,textarea::placeholder{color:${T.textHint};}
        .btn-primary{background:${T.blue};color:#fff;border:none;border-radius:${T.radiusSm};padding:9px 18px;font-size:13px;font-weight:500;cursor:pointer;display:inline-flex;align-items:center;gap:6px;transition:background .15s;}
        .btn-primary:hover{background:${T.blueDark};}
        .btn-ghost{background:transparent;border:1.5px solid ${T.border};border-radius:${T.radiusSm};padding:8px 14px;font-size:13px;color:${T.textSub};cursor:pointer;display:inline-flex;align-items:center;gap:6px;transition:all .15s;}
        .btn-ghost:hover{border-color:${T.blue};color:${T.blue};}
        .card{background:#fff;border-radius:${T.radius};border:1px solid ${T.border};box-shadow:${T.shadow};}
        .page-title{font-size:20px;font-weight:600;color:${T.text};margin:0 0 1.25rem;}
        .grid-2{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
        .grid-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;}
        .page-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:1.25rem;flex-wrap:wrap;gap:10px;}
        .hide-mobile{display:block;}
        .crm-table{overflow-x:auto;-webkit-overflow-scrolling:touch;}
        .modal-overlay{position:fixed;inset:0;background:rgba(241,245,249,0.88);backdrop-filter:blur(4px);z-index:100;display:flex;align-items:center;justify-content:center;padding:1rem;overflow-y:auto;}
        .modal-box{background:#fff;border-radius:16px;border:1px solid ${T.border};box-shadow:0 20px 60px rgba(15,23,42,0.12);width:100%;max-height:92vh;overflow-y:auto;}
        @media(max-width:768px){
          .sidebar{display:none;}
          .main-content{padding:0.75rem;padding-bottom:80px;max-height:none;}
          .mobile-nav{display:flex;position:fixed;bottom:0;left:0;right:0;background:#fff;border-top:1px solid ${T.border};z-index:50;height:64px;align-items:center;justify-content:space-around;padding:0 4px;}
          .welcome-bar{display:flex;}
          .page-title{font-size:17px;margin-bottom:0.875rem;}
          .grid-2{grid-template-columns:1fr;}
          .grid-stats{grid-template-columns:1fr 1fr;}
          .page-header{flex-direction:column;align-items:stretch;}
          .page-header .btn-primary,.page-header .btn-ghost{width:100%;justify-content:center;}
          .hide-mobile{display:none;}
          .modal-overlay{align-items:flex-end;padding:0;overflow-y:auto;}
          .modal-box{border-radius:16px 16px 0 0;max-height:96vh;border-bottom:none;overflow-y:auto;}
          .card{border-radius:10px;}
        }
        @media(max-width:480px){
          .main-content{padding:0.625rem;padding-bottom:76px;}
          .grid-stats{grid-template-columns:1fr 1fr;}
          .grid-2{grid-template-columns:1fr;}
          .modal-box{border-radius:12px 12px 0 0;max-height:96vh;}
        }
      `}</style>
      <div className="app-layout">
        <Sidebar user={user} view={view} setView={setView} logout={logout} notifCount={myNotifs.length}/>
        <main className="main-content">
          <WelcomeBar user={user} logout={logout} notifCount={myNotifs.length} setView={setView}/>
          {pageContent}
        </main>
      </div>
      <MobileNav user={user} view={view} setView={setView} logout={logout} notifCount={myNotifs.length}/>
      {modals}
    </div>
  );
}

/* ─── PASSWORD STRENGTH BAR ─── */
function PasswordStrengthBar({password}){
  const rules=validatePassword(password);
  const score=rules.filter(r=>r.ok).length;
  const ramp=[
    {bar:"#E2E8F0",label:""},
    {bar:"#EF4444",label:"Très faible"},
    {bar:"#EF4444",label:"Faible"},
    {bar:"#F97316",label:"Moyen"},
    {bar:"#3B82F6",label:"Fort"},
    {bar:"#22C55E",label:"Très fort"},
  ];
  const cur=ramp[score]||ramp[0];
  if(!password)return null;
  return(
    <div style={{marginTop:10}}>
      <div style={{display:"flex",gap:4,marginBottom:6}}>
        {[1,2,3,4,5].map(i=>(
          <div key={i} style={{flex:1,height:3,borderRadius:99,background:i<=score?cur.bar:"#E2E8F0",transition:"background .25s"}}/>
        ))}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
        <span style={{fontSize:11,fontWeight:600,color:cur.bar,letterSpacing:".3px"}}>{cur.label}</span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px 12px"}}>
        {rules.map((r,i)=>(
          <span key={i} style={{fontSize:11,color:r.ok?"#059669":"#94A3B8",display:"flex",alignItems:"center",gap:4,transition:"color .2s"}}>
            <i className={`ti ${r.ok?"ti-circle-check":"ti-circle"}`} style={{fontSize:13,color:r.ok?"#22C55E":"#CBD5E1",flexShrink:0}} aria-hidden/>
            {r.msg}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── LOGIN ─── */
function LoginPage({form,setForm,onLogin}){
  const [showPwd,setShowPwd]=useState(false);
  const features=[
    {icon:"ti-calendar-check",title:"Planification",desc:"Gérez tous vos RDV d'installation"},
    {icon:"ti-users",title:"CRM Clients",desc:"Suivi commercial centralisé"},
    {icon:"ti-clipboard-check",title:"Validation terrain",desc:"Photos & signature client"},
    {icon:"ti-shield-check",title:"Sécurisé",desc:"Connexion chiffrée SHA-256"},
  ];
  return(
    <div style={{minHeight:"100vh",display:"flex",fontFamily:"var(--font-sans)",background:"#F8FAFC"}}>
      {/* ── Panneau gauche décoratif ── */}
      <div className="hide-mobile" style={{flex:1,background:"#0F172A",display:"flex",flexDirection:"column",justifyContent:"space-between",padding:"3rem",position:"relative",overflow:"hidden"}}>
        {/* Cercles décoratifs */}
        <div style={{position:"absolute",top:-120,right:-120,width:400,height:400,borderRadius:"50%",background:"rgba(37,99,235,.12)"}}/>
        <div style={{position:"absolute",bottom:-80,left:-80,width:300,height:300,borderRadius:"50%",background:"rgba(37,99,235,.08)"}}/>
        <div style={{position:"absolute",top:"40%",left:"60%",width:180,height:180,borderRadius:"50%",background:"rgba(37,99,235,.06)"}}/>

        {/* Logo & titre */}
        <div style={{position:"relative"}}>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:"3rem"}}>
            <div style={{width:40,height:40,borderRadius:10,background:"#2563EB",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <i className="ti ti-wifi" style={{fontSize:20,color:"#fff"}} aria-hidden/>
            </div>
            <span style={{fontSize:16,fontWeight:600,color:"#fff",letterSpacing:"-.2px"}}>Réparation WiFi</span>
          </div>
          <h1 style={{fontSize:36,fontWeight:700,color:"#fff",margin:"0 0 14px",lineHeight:1.2,letterSpacing:"-1px"}}>
            Gérez vos<br/>
            <span style={{color:"#60A5FA"}}>interventions</span><br/>
            sereinement.
          </h1>
          <p style={{fontSize:14,color:"#94A3B8",margin:0,lineHeight:1.7,maxWidth:340}}>
            Planification, suivi client, validation terrain — tout en un seul endroit.
          </p>
        </div>

        {/* Feature cards */}
        <div style={{position:"relative",display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          {features.map(f=>(
            <div key={f.icon} style={{background:"rgba(255,255,255,.05)",border:"0.5px solid rgba(255,255,255,.08)",borderRadius:12,padding:"14px 16px"}}>
              <i className={`ti ${f.icon}`} style={{fontSize:20,color:"#60A5FA",display:"block",marginBottom:8}} aria-hidden/>
              <p style={{fontSize:13,fontWeight:500,color:"#fff",margin:"0 0 3px"}}>{f.title}</p>
              <p style={{fontSize:11,color:"#64748B",margin:0,lineHeight:1.5}}>{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{position:"relative",marginTop:"2rem"}}>
          <p style={{fontSize:11,color:"#334155",margin:0}}>© 2025 Réparation WiFi · Accès réservé au personnel autorisé</p>
        </div>
      </div>

      {/* ── Panneau droit formulaire ── */}
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"2rem 1.25rem",background:"#fff"}}>
        <div style={{width:"100%",maxWidth:360}}>
          {/* En-tête */}
          <div style={{marginBottom:36}}>
            <div style={{width:48,height:48,borderRadius:12,background:"#EFF6FF",border:"1px solid #BFDBFE",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:20}}>
              <i className="ti ti-lock" style={{fontSize:22,color:"#2563EB"}} aria-hidden/>
            </div>
            <h2 style={{fontSize:24,fontWeight:700,margin:"0 0 6px",color:"#0F172A",letterSpacing:"-.5px"}}>Connexion</h2>
            <p style={{fontSize:14,color:"#64748B",margin:0}}>Entrez vos identifiants pour accéder à la plateforme</p>
          </div>

          {/* Champs */}
          <div style={{display:"flex",flexDirection:"column",gap:18}}>
            <div>
              <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:7,letterSpacing:".2px"}}>Nom de famille</label>
              <div style={{position:"relative"}}>
                <i className="ti ti-user" style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",fontSize:17,color:"#9CA3AF",pointerEvents:"none"}} aria-hidden/>
                <input
                  value={form.lastName}
                  onChange={e=>setForm(f=>({...f,lastName:e.target.value,error:""}))}
                  placeholder="Ex: Tahiri"
                  autoComplete="username"
                  maxLength={50}
                  style={{width:"100%",paddingLeft:40,boxSizing:"border-box",height:44,border:"1.5px solid #E5E7EB",borderRadius:10,fontSize:14,outline:"none",transition:"border .15s",background:"#F9FAFB"}}
                  onFocus={e=>e.target.style.border="1.5px solid #2563EB"}
                  onBlur={e=>e.target.style.border="1.5px solid #E5E7EB"}
                  onKeyDown={e=>e.key==="Enter"&&onLogin()}
                />
              </div>
            </div>
            <div>
              <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:7,letterSpacing:".2px"}}>Mot de passe</label>
              <div style={{position:"relative"}}>
                <i className="ti ti-key" style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",fontSize:17,color:"#9CA3AF",pointerEvents:"none"}} aria-hidden/>
                <input
                  value={form.password}
                  onChange={e=>setForm(f=>({...f,password:e.target.value,error:""}))}
                  placeholder="••••••••••"
                  type={showPwd?"text":"password"}
                  autoComplete="current-password"
                  maxLength={128}
                  style={{width:"100%",paddingLeft:40,paddingRight:44,boxSizing:"border-box",height:44,border:"1.5px solid #E5E7EB",borderRadius:10,fontSize:14,outline:"none",transition:"border .15s",background:"#F9FAFB"}}
                  onFocus={e=>e.target.style.border="1.5px solid #2563EB"}
                  onBlur={e=>e.target.style.border="1.5px solid #E5E7EB"}
                  onKeyDown={e=>e.key==="Enter"&&onLogin()}
                />
                <button onClick={()=>setShowPwd(v=>!v)} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"transparent",border:"none",cursor:"pointer",color:"#9CA3AF",display:"flex",alignItems:"center",padding:0}}>
                  <i className={`ti ${showPwd?"ti-eye-off":"ti-eye"}`} style={{fontSize:17}} aria-hidden/>
                </button>
              </div>
            </div>

            {/* Erreur */}
            {form.error&&(
              <div style={{background:"#FEF2F2",border:"1px solid #FCA5A5",borderRadius:10,padding:"11px 14px",fontSize:13,color:"#B91C1C",display:"flex",alignItems:"center",gap:9}}>
                <i className="ti ti-alert-triangle" style={{fontSize:17,flexShrink:0}} aria-hidden/>{form.error}
              </div>
            )}

            {/* Bouton connexion */}
            <button
              onClick={onLogin}
              disabled={form.loading}
              style={{width:"100%",height:46,background:form.loading?"#93C5FD":"#2563EB",color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:600,cursor:form.loading?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:4,letterSpacing:".2px",transition:"background .15s"}}
              onMouseEnter={e=>!form.loading&&(e.target.style.background="#1D4ED8")}
              onMouseLeave={e=>!form.loading&&(e.target.style.background="#2563EB")}
            >
              {form.loading
                ?<><i className="ti ti-loader-2" style={{fontSize:17,animation:"rwspin 1s linear infinite"}} aria-hidden/> Vérification en cours…</>
                :<><i className="ti ti-login" style={{fontSize:17}} aria-hidden/> Se connecter</>
              }
            </button>
          </div>

          {/* Note premier login */}
          <div style={{marginTop:28,padding:"12px 16px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,display:"flex",alignItems:"flex-start",gap:10}}>
            <i className="ti ti-info-circle" style={{fontSize:17,color:"#D97706",flexShrink:0,marginTop:1}} aria-hidden/>
            <p style={{fontSize:12,color:"#92400E",margin:0,lineHeight:1.6}}>
              <strong>Première connexion ?</strong> Entrez uniquement votre nom de famille — vous serez guidé pour créer votre mot de passe.
            </p>
          </div>
        </div>
      </div>
      <style>{`@keyframes rwspin{to{transform:rotate(360deg)}} @media(max-width:768px){.rw-left{display:none!important}}`}</style>
    </div>
  );
}

/* ─── FIRST LOGIN PAGE ─── */
function FirstLoginPage({user,onSubmit,onBack}){
  const [pwd,setPwd]=useState("");
  const [pwd2,setPwd2]=useState("");
  const [show,setShow]=useState(false);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const strong=isPasswordStrong(pwd);
  const match=pwd===pwd2&&pwd2.length>0;
  const prenom=user?.name?.split(" ")[0]||"";
  const step1done=strong;
  const step2done=match;

  async function handleSubmit(){
    if(!strong){setError("Le mot de passe ne respecte pas encore tous les critères.");return;}
    if(!match){setError("Les deux mots de passe ne correspondent pas.");return;}
    setLoading(true);
    await onSubmit(pwd);
    setLoading(false);
  }

  const inputStyle={width:"100%",paddingLeft:42,paddingRight:44,boxSizing:"border-box",height:44,border:"1.5px solid #E5E7EB",borderRadius:10,fontSize:14,outline:"none",background:"#F9FAFB"};

  return(
    <div style={{minHeight:"100vh",background:"#0F172A",display:"flex",alignItems:"center",justifyContent:"center",padding:"1.5rem",fontFamily:"var(--font-sans)",position:"relative",overflow:"hidden"}}>
      {/* Déco */}
      <div style={{position:"absolute",top:-150,right:-150,width:500,height:500,borderRadius:"50%",background:"rgba(37,99,235,.1)"}}/>
      <div style={{position:"absolute",bottom:-100,left:-100,width:350,height:350,borderRadius:"50%",background:"rgba(37,99,235,.07)"}}/>

      <div style={{width:"100%",maxWidth:480,position:"relative"}}>
        {/* Card principale */}
        <div style={{background:"#fff",borderRadius:20,overflow:"hidden",boxShadow:"0 32px 80px rgba(0,0,0,.35)"}}>

          {/* Header coloré */}
          <div style={{background:"#0F172A",padding:"28px 32px 24px",borderBottom:"1px solid rgba(255,255,255,.08)"}}>
            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:16}}>
              <div style={{width:48,height:48,borderRadius:14,background:"#2563EB",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <i className="ti ti-shield-lock" style={{fontSize:23,color:"#fff"}} aria-hidden/>
              </div>
              <div>
                <p style={{fontSize:11,color:"#60A5FA",margin:"0 0 2px",fontWeight:600,textTransform:"uppercase",letterSpacing:"1px"}}>Première connexion</p>
                <h2 style={{fontSize:18,fontWeight:700,color:"#fff",margin:0,letterSpacing:"-.3px"}}>Créez votre mot de passe</h2>
              </div>
            </div>
            <div style={{background:"rgba(96,165,250,.1)",border:"1px solid rgba(96,165,250,.2)",borderRadius:10,padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:34,height:34,borderRadius:8,background:"#1E3A5F",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:13,fontWeight:700,color:"#60A5FA"}}>
                {prenom[0]?.toUpperCase()||"?"}
              </div>
              <div>
                <p style={{fontSize:13,fontWeight:500,color:"#fff",margin:0}}>{user?.name}</p>
                <p style={{fontSize:11,color:"#64748B",margin:0,textTransform:"capitalize"}}>{user?.role}</p>
              </div>
            </div>
          </div>

          {/* Corps formulaire */}
          <div style={{padding:"28px 32px",display:"flex",flexDirection:"column",gap:20}}>

            {/* Étapes visuelles */}
            <div style={{display:"flex",gap:0,marginBottom:4}}>
              {[{n:1,label:"Choisir",done:step1done},{n:2,label:"Confirmer",done:step2done}].map((s,i,arr)=>(
                <React.Fragment key={s.n}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{width:28,height:28,borderRadius:"50%",background:s.done?"#2563EB":pwd||pwd2?"#E5E7EB":"#F3F4F6",border:s.done?"none":"1.5px solid #E5E7EB",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .2s"}}>
                      {s.done
                        ?<i className="ti ti-check" style={{fontSize:13,color:"#fff"}} aria-hidden/>
                        :<span style={{fontSize:11,fontWeight:600,color:"#9CA3AF"}}>{s.n}</span>
                      }
                    </div>
                    <span style={{fontSize:12,fontWeight:s.done?600:400,color:s.done?"#2563EB":"#9CA3AF"}}>{s.label}</span>
                  </div>
                  {i<arr.length-1&&<div style={{flex:1,height:1,background:step1done?"#BFDBFE":"#E5E7EB",margin:"0 12px",alignSelf:"center",transition:"background .3s"}}/>}
                </React.Fragment>
              ))}
            </div>

            {/* Champ mot de passe */}
            <div>
              <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:7}}>Nouveau mot de passe</label>
              <div style={{position:"relative"}}>
                <i className="ti ti-key" style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",fontSize:17,color:"#9CA3AF",pointerEvents:"none"}} aria-hidden/>
                <input
                  value={pwd} onChange={e=>setPwd(e.target.value)} type={show?"text":"password"}
                  placeholder="Choisissez un mot de passe fort" autoComplete="new-password" maxLength={128}
                  style={{...inputStyle,paddingRight:44,border:pwd&&!strong?"1.5px solid #EF4444":pwd&&strong?"1.5px solid #22C55E":"1.5px solid #E5E7EB"}}
                />
                <button onClick={()=>setShow(v=>!v)} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"transparent",border:"none",cursor:"pointer",color:"#9CA3AF",display:"flex",alignItems:"center",padding:0}}>
                  <i className={`ti ${show?"ti-eye-off":"ti-eye"}`} style={{fontSize:17}} aria-hidden/>
                </button>
              </div>
              <PasswordStrengthBar password={pwd}/>
            </div>

            {/* Champ confirmation */}
            <div>
              <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:7}}>Confirmer le mot de passe</label>
              <div style={{position:"relative"}}>
                <i className="ti ti-lock-check" style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",fontSize:17,color:"#9CA3AF",pointerEvents:"none"}} aria-hidden/>
                <input
                  value={pwd2} onChange={e=>setPwd2(e.target.value)} type={show?"text":"password"}
                  placeholder="Répétez le mot de passe" autoComplete="new-password" maxLength={128}
                  style={{...inputStyle,border:pwd2&&!match?"1.5px solid #EF4444":pwd2&&match?"1.5px solid #22C55E":"1.5px solid #E5E7EB"}}
                  onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
                />
              </div>
              {pwd2&&match&&(
                <p style={{fontSize:11,color:"#059669",margin:"6px 0 0",display:"flex",alignItems:"center",gap:4}}>
                  <i className="ti ti-circle-check" style={{fontSize:13,color:"#22C55E"}} aria-hidden/> Mots de passe identiques
                </p>
              )}
              {pwd2&&!match&&(
                <p style={{fontSize:11,color:"#DC2626",margin:"6px 0 0",display:"flex",alignItems:"center",gap:4}}>
                  <i className="ti ti-circle-x" style={{fontSize:13,color:"#EF4444"}} aria-hidden/> Ne correspondent pas
                </p>
              )}
            </div>

            {/* Erreur globale */}
            {error&&(
              <div style={{background:"#FEF2F2",border:"1px solid #FCA5A5",borderRadius:10,padding:"11px 14px",fontSize:13,color:"#B91C1C",display:"flex",alignItems:"center",gap:9}}>
                <i className="ti ti-alert-triangle" style={{fontSize:17,flexShrink:0}} aria-hidden/>{error}
              </div>
            )}

            {/* Boutons */}
            <div style={{display:"flex",gap:10,marginTop:4}}>
              <button onClick={onBack} style={{flex:1,height:44,background:"#F8FAFC",color:"#374151",border:"1.5px solid #E5E7EB",borderRadius:10,fontSize:13,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                <i className="ti ti-arrow-left" style={{fontSize:15}} aria-hidden/> Retour
              </button>
              <button
                onClick={handleSubmit}
                disabled={!strong||!match||loading}
                style={{flex:2,height:44,background:strong&&match&&!loading?"#2563EB":"#CBD5E1",color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:600,cursor:strong&&match&&!loading?"pointer":"not-allowed",display:"flex",alignItems:"center",justifyContent:"center",gap:8,transition:"background .2s"}}
              >
                {loading
                  ?<><i className="ti ti-loader-2" style={{fontSize:16,animation:"rwspin 1s linear infinite"}} aria-hidden/> Création…</>
                  :<><i className="ti ti-shield-check" style={{fontSize:16}} aria-hidden/> Créer mon accès</>
                }
              </button>
            </div>
          </div>
        </div>

        {/* Note sécurité */}
        <p style={{textAlign:"center",fontSize:12,color:"#334155",marginTop:20,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
          <i className="ti ti-lock" style={{fontSize:14}} aria-hidden/> Connexion chiffrée · Ce mot de passe ne peut pas être récupéré
        </p>
      </div>
      <style>{`@keyframes rwspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

/* ─── SIDEBAR ─── */
function Sidebar({user,view,setView,logout,notifCount}){
  const links = user?.role==="admin"
    ?[{id:"dashboard",label:"Dashboard",icon:"ti-layout-dashboard"},{id:"calendrier",label:"Calendrier",icon:"ti-calendar"},{id:"crm",label:"CRM Clients",icon:"ti-users"},{id:"liste",label:"Interventions",icon:"ti-list"},{id:"utilisateurs",label:"Utilisateurs",icon:"ti-user-cog"},{id:"notifications",label:"Notifications",icon:"ti-bell"}]
    :user?.role==="commercial"
    ?[{id:"calendrier",label:"Calendrier",icon:"ti-calendar"},{id:"crm",label:"CRM Clients",icon:"ti-users"},{id:"liste",label:"Interventions",icon:"ti-list"},{id:"notifications",label:"Notifications",icon:"ti-bell"}]
    :[{id:"calendrier",label:"Mon planning",icon:"ti-calendar"},{id:"liste",label:"Mes interventions",icon:"ti-list"},{id:"notifications",label:"Notifications",icon:"ti-bell"}];

  const roleLabel = user?.role==="admin"?"Administrateur":user?.role==="commercial"?"Commercial":"Technicien";
  const roleColors = {admin:["#7C3AED","#F5F3FF"],commercial:[T.blue,T.blueLight],technicien:["#059669","#ECFDF5"]};
  const [rc,rbg] = roleColors[user?.role]||[T.blue,T.blueLight];

  return(
    <div className="sidebar" style={{fontFamily:"var(--font-sans)"}}>
      {/* Brand */}
      <div style={{padding:"20px 20px 18px",borderBottom:`1px solid ${T.border}`}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:34,height:34,borderRadius:10,background:`linear-gradient(135deg,${T.blue},${T.blueDark})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <i className="ti ti-wifi" style={{fontSize:17,color:"#fff"}} aria-hidden/>
          </div>
          <div>
            <p style={{fontSize:14,fontWeight:600,margin:0,color:T.text,letterSpacing:"-.3px"}}>Réparation WiFi</p>
            <p style={{fontSize:10,color:T.textHint,margin:0,textTransform:"uppercase",letterSpacing:".5px"}}>Serrures digitales</p>
          </div>
        </div>
      </div>

      {/* User */}
      <div style={{padding:"18px 20px",borderBottom:`1px solid ${T.border}`}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:38,height:38,borderRadius:"50%",background:`linear-gradient(135deg,${T.blue},${T.blueDark})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:600,color:"#fff",flexShrink:0}}>{user?.avatar}</div>
          <div style={{flex:1,minWidth:0}}>
            <p style={{fontSize:13,fontWeight:500,margin:0,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.name}</p>
            <span style={{fontSize:11,background:rbg,color:rc,borderRadius:99,padding:"1px 8px",fontWeight:500}}>{roleLabel}</span>
          </div>
        </div>
      </div>

      {/* Nav links */}
      <nav style={{flex:1,padding:"12px 10px",display:"flex",flexDirection:"column",gap:2}}>
        <p style={{fontSize:10,fontWeight:600,color:T.textHint,textTransform:"uppercase",letterSpacing:".8px",margin:"0 8px 8px"}}>Navigation</p>
        {links.map(l=>{
          const active=view===l.id;
          return(
            <button key={l.id} onClick={()=>setView(l.id)} style={{position:"relative",border:"none",background:active?T.blueLight:"transparent",color:active?T.blue:T.textSub,borderRadius:T.radiusSm,padding:"10px 12px",cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",gap:10,width:"100%",textAlign:"left",fontWeight:active?500:400,transition:"all .15s",borderLeft:active?`3px solid ${T.blue}`:"3px solid transparent"}}>
              <i className={`ti ${l.icon}`} style={{fontSize:16,flexShrink:0}} aria-hidden/>
              <span style={{flex:1}}>{l.label}</span>
              {l.id==="notifications"&&notifCount>0&&<span style={{background:"#EF4444",color:"#fff",borderRadius:99,padding:"1px 7px",fontSize:10,fontWeight:600,minWidth:18,textAlign:"center"}}>{notifCount}</span>}
            </button>
          );
        })}
      </nav>

      {/* Logout */}
      <div style={{padding:"12px 10px",borderTop:`1px solid ${T.border}`}}>
        <button onClick={logout} style={{border:"none",background:"transparent",color:T.textSub,borderRadius:T.radiusSm,padding:"10px 12px",cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",gap:10,width:"100%",transition:"all .15s"}}
          onMouseEnter={e=>{e.currentTarget.style.background="#FEF2F2";e.currentTarget.style.color="#EF4444";}}
          onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color=T.textSub;}}>
          <i className="ti ti-logout" style={{fontSize:16}} aria-hidden/>
          <span>Déconnexion</span>
        </button>
      </div>
    </div>
  );
}

/* ─── MOBILE NAV ─── */
/* ─── WELCOME BAR (mobile only) ─── */
function WelcomeBar({user,logout,notifCount,setView}){
  const hour=new Date().getHours();
  const greet=hour<12?"Bonjour":hour<18?"Bon après-midi":"Bonsoir";
  const roleLabel={admin:"Administrateur",commercial:"Commercial",technicien:"Technicien"}[user?.role]||"";
  const roleColors={admin:["#7C3AED","#F5F3FF"],commercial:[T.blue,T.blueLight],technicien:["#059669","#ECFDF5"]};
  const [rc,rbg]=roleColors[user?.role]||[T.blue,T.blueLight];
  return(
    <div className="welcome-bar" style={{alignItems:"center",justifyContent:"space-between",background:`linear-gradient(135deg,#0F172A,#1E3A5F)`,borderRadius:T.radius,padding:"14px 16px",marginBottom:"1rem",gap:12}}>
      <div style={{display:"flex",alignItems:"center",gap:12,flex:1,minWidth:0}}>
        <div style={{width:40,height:40,borderRadius:"50%",background:`linear-gradient(135deg,${T.blue},${T.blueDark})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:700,color:"#fff",flexShrink:0}}>
          {user?.avatar}
        </div>
        <div style={{minWidth:0}}>
          <p style={{fontSize:13,color:"#94A3B8",margin:0}}>{greet} 👋</p>
          <p style={{fontSize:15,fontWeight:700,color:"#fff",margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.name}</p>
          <span style={{fontSize:10,background:rbg,color:rc,borderRadius:99,padding:"1px 8px",fontWeight:600}}>{roleLabel}</span>
        </div>
      </div>
      <div style={{display:"flex",gap:8,flexShrink:0}}>
        <button onClick={()=>setView("notifications")} style={{width:36,height:36,borderRadius:"50%",background:"rgba(255,255,255,.1)",border:"1px solid rgba(255,255,255,.15)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",position:"relative"}}>
          <i className="ti ti-bell" style={{fontSize:17}} aria-hidden/>
          {notifCount>0&&<span style={{position:"absolute",top:2,right:2,background:"#EF4444",color:"#fff",borderRadius:"50%",width:14,height:14,fontSize:8,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700}}>{notifCount}</span>}
        </button>
        <button onClick={logout} style={{width:36,height:36,borderRadius:"50%",background:"rgba(239,68,68,.2)",border:"1px solid rgba(239,68,68,.3)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#FCA5A5"}} title="Déconnexion">
          <i className="ti ti-logout" style={{fontSize:17}} aria-hidden/>
        </button>
      </div>
    </div>
  );
}

/* ─── MOBILE NAV ─── */
function MobileNav({user,view,setView,notifCount}){
  const links = user?.role==="admin"
    ?[{id:"dashboard",icon:"ti-layout-dashboard",label:"Dashboard"},{id:"calendrier",icon:"ti-calendar",label:"Calendrier"},{id:"crm",icon:"ti-users",label:"CRM"},{id:"liste",icon:"ti-list",label:"Interventions"},{id:"notifications",icon:"ti-bell",label:"Notifs"}]
    :user?.role==="commercial"
    ?[{id:"calendrier",icon:"ti-calendar",label:"Calendrier"},{id:"crm",icon:"ti-users",label:"CRM"},{id:"liste",icon:"ti-list",label:"Interventions"},{id:"notifications",icon:"ti-bell",label:"Notifs"}]
    :[{id:"calendrier",icon:"ti-calendar",label:"Planning"},{id:"liste",icon:"ti-list",label:"Interventions"},{id:"notifications",icon:"ti-bell",label:"Notifs"}];
  return(
    <div className="mobile-nav">
      {links.map(l=>(
        <button key={l.id} onClick={()=>setView(l.id)} style={{position:"relative",border:"none",background:"transparent",color:view===l.id?T.blue:T.textHint,padding:"4px 2px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3,flex:1,transition:"color .15s",minWidth:0}}>
          {view===l.id&&<span style={{position:"absolute",top:-1,left:"50%",transform:"translateX(-50%)",width:24,height:2,borderRadius:99,background:T.blue}}/>}
          <i className={`ti ${l.icon}`} style={{fontSize:21}} aria-hidden/>
          <span style={{fontSize:9,fontWeight:view===l.id?700:400,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:52,textAlign:"center"}}>{l.label}</span>
          {l.id==="notifications"&&notifCount>0&&<span style={{position:"absolute",top:2,right:"calc(50% - 18px)",background:"#EF4444",color:"#fff",borderRadius:"50%",width:15,height:15,fontSize:8,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700}}>{notifCount}</span>}
        </button>
      ))}
    </div>
  );
}

/* ─── DASHBOARD ─── */
function Dashboard({interventions,users,leads,onOpen}){
  const today=D(0);
  const todayInts=interventions.filter(i=>i.date===today);
  const upcoming=interventions.filter(i=>i.date>today).sort((a,b)=>a.date.localeCompare(b.date));
  const done=interventions.filter(i=>i.statut==="fait");
  const inProg=interventions.filter(i=>i.statut==="encours");
  const cancelled=interventions.filter(i=>i.statut==="annule");
  const ca=done.filter(i=>i.prix).reduce((s,i)=>s+parseFloat((i.prix||"0").replace(/\s/g,"")),0);
  const leadsGagnes=leads.filter(l=>l.statut==="gagne").length;

  const stats=[
    {label:"Total interventions",value:interventions.length,icon:"ti-tools",color:T.blue,bg:T.blueLight},
    {label:"Installations faites",value:done.length,icon:"ti-circle-check",color:"#059669",bg:"#ECFDF5"},
    {label:"En cours",value:inProg.length,icon:"ti-clock",color:"#D97706",bg:"#FFFBEB"},
    {label:"Annulées",value:cancelled.length,icon:"ti-x",color:"#64748B",bg:"#F8FAFC"},
    {label:"Leads gagnés",value:leadsGagnes,icon:"ti-star",color:"#7C3AED",bg:"#F5F3FF"},
    {label:"CA réalisé",value:ca.toLocaleString("fr-FR")+" MAD",icon:"ti-trending-up",color:"#0891B2",bg:"#ECFEFF"},
  ];

  return(
    <div>
      <div style={{marginBottom:"1rem"}}>
        <h2 className="page-title" style={{marginBottom:4}}>Tableau de bord</h2>
        <p style={{fontSize:13,color:T.textSub,margin:0}}>Bienvenue — {new Date().toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</p>
      </div>
      <div className="grid-stats" style={{marginBottom:"1.25rem"}}>
        {stats.map(s=>(
          <div key={s.label} className="card" style={{padding:"12px 14px",display:"flex",alignItems:"flex-start",gap:12}}>
            <div style={{width:36,height:36,borderRadius:9,background:s.bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <i className={`ti ${s.icon}`} style={{fontSize:17,color:s.color}} aria-hidden/>
            </div>
            <div>
              <p style={{fontSize:11,color:T.textSub,margin:"0 0 4px",fontWeight:500,textTransform:"uppercase",letterSpacing:".4px"}}>{s.label}</p>
              <p style={{fontSize:19,fontWeight:700,color:T.text,margin:0,letterSpacing:"-.5px"}}>{s.value}</p>
            </div>
          </div>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))",gap:"0.875rem"}}>
        <Section title="Aujourd'hui" count={todayInts.length} color={T.blue}>{todayInts.length===0?<Empty text="Aucune intervention aujourd'hui"/>:todayInts.map(i=><IntCard key={i.id} i={i} users={users} onClick={()=>onOpen(i)}/>)}</Section>
        <Section title="À venir" count={upcoming.length} color="#7C3AED">{upcoming.length===0?<Empty text="Aucune intervention à venir"/>:upcoming.slice(0,4).map(i=><IntCard key={i.id} i={i} users={users} onClick={()=>onOpen(i)}/>)}</Section>
        <Section title="Annulées" count={cancelled.length} color={T.textSub}>{cancelled.length===0?<Empty text="Aucune annulation"/>:cancelled.slice(0,4).map(i=><IntCard key={i.id} i={i} users={users} onClick={()=>onOpen(i)}/>)}</Section>
      </div>
    </div>
  );
}

/* ─── CRM VIEW ─── */
function CrmView({leads,serrures,users,onNewLead,onEditLead,onCreateRdv,onSendSerrure,onManageSerrures,deleteLead,notifications}){
  const [tab,setTab]=useState("leads");
  const [search,setSearch]=useState("");
  const [filterStatut,setFilterStatut]=useState("tous");
  const today=D(0);

  // Séparation rappels du jour vs reste
  const rappelsToday=leads.filter(l=>l.rappelDate===today&&l.statut!=="gagne"&&l.statut!=="perdu");
  const autresLeads=leads.filter(l=>!(l.rappelDate===today&&l.statut!=="gagne"&&l.statut!=="perdu"));

  const filtered=autresLeads
    .filter(l=>filterStatut==="tous"||l.statut===filterStatut)
    .filter(l=>!search||`${l.nom} ${l.prenom} ${l.telephone} ${l.adresse}`.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b)=>b.createdAt?.localeCompare(a.createdAt||"")||0);

  const rappelsFiltres=rappelsToday
    .filter(l=>filterStatut==="tous"||l.statut===filterStatut)
    .filter(l=>!search||`${l.nom} ${l.prenom} ${l.telephone} ${l.adresse}`.toLowerCase().includes(search.toLowerCase()));

  const counts=Object.fromEntries(Object.keys(LEAD_COLORS).map(k=>[k,leads.filter(l=>l.statut===k).length]));

  const TABLE_COLS="2fr 1.2fr 1.5fr 1.4fr 1fr 160px";
  const isMob=typeof window!=="undefined"&&window.innerWidth<768;
  const TABLE_HEADERS=["Client","Téléphone","Serrure souhaitée","Rappel","Statut","Actions"];

  return(
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{marginBottom:4}}>CRM — Suivi commercial</h2>
          <p style={{fontSize:13,color:T.textSub,margin:0}}>
            {leads.length} client{leads.length>1?"s":""} au total
            {rappelsToday.length>0&&<span style={{marginLeft:12,color:"#D97706",fontWeight:600,display:"inline-flex",alignItems:"center",gap:5,background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:99,padding:"2px 10px"}}><i className="ti ti-alarm" style={{fontSize:13}} aria-hidden/>{rappelsToday.length} rappel{rappelsToday.length>1?"s":""} aujourd'hui</span>}
          </p>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={onManageSerrures} className="btn-ghost">
            <i className="ti ti-lock-cog" style={{fontSize:15}} aria-hidden/> Gérer les serrures
          </button>
          <button onClick={onNewLead} className="btn-primary">
            <i className="ti ti-user-plus" style={{fontSize:15}} aria-hidden/> Nouveau client
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:0,marginBottom:"1.25rem",background:T.pageBg,borderRadius:T.radiusSm,padding:4,width:"fit-content",border:`1px solid ${T.border}`}}>
        {[{id:"leads",label:"Clients",icon:"ti-users"},{id:"catalogue",label:"Catalogue serrures",icon:"ti-lock"}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{background:tab===t.id?"#fff":"transparent",border:"none",borderRadius:T.radiusSm,padding:"7px 16px",fontSize:13,cursor:"pointer",fontWeight:tab===t.id?500:400,color:tab===t.id?T.blue:T.textSub,display:"flex",alignItems:"center",gap:6,boxShadow:tab===t.id?T.shadow:"none",transition:"all .15s"}}>
            <i className={`ti ${t.icon}`} style={{fontSize:14}} aria-hidden/>{t.label}
          </button>
        ))}
      </div>

      {tab==="leads" && (
        <>
          {/* Filtres */}
          <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:"1.25rem"}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher nom, téléphone, adresse…" style={{width:"100%"}}/>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              <button onClick={()=>setFilterStatut("tous")} style={{background:filterStatut==="tous"?T.blue:"#fff",color:filterStatut==="tous"?"#fff":T.textSub,border:`1px solid ${filterStatut==="tous"?T.blue:T.border}`,borderRadius:99,padding:"5px 12px",fontSize:11,cursor:"pointer",fontWeight:filterStatut==="tous"?500:400,transition:"all .15s"}}>
                Tous <span style={{opacity:.7}}>({leads.length})</span>
              </button>
              {Object.entries(LEAD_COLORS).map(([k,c])=>(
                <button key={k} onClick={()=>setFilterStatut(k)} style={{background:filterStatut===k?c.bg:"#fff",color:filterStatut===k?c.text:T.textSub,border:`1px solid ${filterStatut===k?c.border:T.border}`,borderRadius:99,padding:"5px 12px",fontSize:11,cursor:"pointer",fontWeight:filterStatut===k?500:400,transition:"all .15s",display:"inline-flex",alignItems:"center",gap:5}}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:c.dot,display:"inline-block"}}/>
                  {c.label} <span style={{opacity:.6}}>({counts[k]||0})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Rappels du jour */}
          {rappelsFiltres.length>0&&(
            <div style={{marginBottom:"1.5rem"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:"0.75rem"}}>
                <div style={{display:"flex",alignItems:"center",gap:7,background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:T.radiusSm,padding:"6px 14px"}}>
                  <i className="ti ti-alarm" style={{fontSize:16,color:"#D97706"}} aria-hidden/>
                  <span style={{fontSize:13,fontWeight:600,color:"#92400E"}}>Rappels téléphoniques du jour</span>
                  <span style={{background:"#D97706",color:"#fff",borderRadius:99,width:20,height:20,fontSize:11,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>{rappelsFiltres.length}</span>
                </div>
                <div style={{flex:1,height:1,background:"#FDE68A"}}/>
              </div>
              <div className="crm-table">
                <div style={{background:"#FFFBEB",border:"2px solid #FDE68A",borderRadius:T.radius,overflow:"hidden"}}>
                  <div style={{display:"grid",gridTemplateColumns:TABLE_COLS,gap:0,background:"#FEF3C7",borderBottom:"1px solid #FDE68A",padding:"10px 16px",minWidth:700}}>
                    {TABLE_HEADERS.map(h=>(
                      <span key={h} style={{fontSize:11,fontWeight:600,color:"#92400E",textTransform:"uppercase",letterSpacing:".5px"}}>{h}</span>
                    ))}
                  </div>
                  {rappelsFiltres.map((lead,idx)=>(
                    <LeadRow key={lead.id} lead={lead} serrures={serrures} today={today}
                      onEdit={()=>onEditLead(lead)} onCreateRdv={()=>onCreateRdv(lead)}
                      onSendSerrure={s=>onSendSerrure(s,lead)}
                      idx={idx} total={rappelsFiltres.length} rappelMode/>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Table principale */}
          {filtered.length===0&&rappelsFiltres.length===0
            ?<Empty text="Aucun client trouvé"/>
            :filtered.length>0&&(
              <div>
                {rappelsFiltres.length>0&&(
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:"0.75rem"}}>
                    <span style={{fontSize:12,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:".5px"}}>Tous les clients</span>
                    <div style={{flex:1,height:1,background:T.border}}/>
                  </div>
                )}
                <div className="crm-table">
                  <div className="card" style={{overflow:"hidden"}}>
                    <div style={{display:"grid",gridTemplateColumns:TABLE_COLS,gap:0,background:T.pageBg,borderBottom:`1px solid ${T.border}`,padding:"10px 16px",minWidth:700}}>
                      {TABLE_HEADERS.map(h=>(
                        <span key={h} style={{fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:".5px"}}>{h}</span>
                      ))}
                    </div>
                    {filtered.map((lead,idx)=>(
                      <LeadRow key={lead.id} lead={lead} serrures={serrures} today={today}
                        onEdit={()=>onEditLead(lead)} onCreateRdv={()=>onCreateRdv(lead)}
                        onSendSerrure={s=>onSendSerrure(s,lead)}
                        idx={idx} total={filtered.length}/>
                    ))}
                  </div>
                </div>
              </div>
            )
          }
        </>
      )}

      {tab==="catalogue" && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:"0.875rem"}}>
          {serrures.map(s=><SerrureCatalogCard key={s.id} serrure={s} leads={leads} onSend={lead=>onSendSerrure(s,lead)}/>)}
        </div>
      )}
    </div>
  );
}

/* ─── LEAD ROW (vue tableau) ─── */
function LeadRow({lead,serrures,today,onEdit,onCreateRdv,onSendSerrure,idx,total,rappelMode}){
  const c=LEAD_COLORS[lead.statut]||LEAD_COLORS.nouveau;
  const serrure=serrures.find(s=>s.id===lead.serrureId);
  const isRappelToday=lead.rappelDate===today&&lead.statut!=="gagne"&&lead.statut!=="perdu";
  const isLast=idx===total-1;
  const wa=`https://wa.me/212${lead.telephone?.replace(/^0/,"").replace(/\s/g,"")}`;
  const [hovered,setHovered]=useState(false);
  const bgNormal=rappelMode?"#FFFBEB":"#fff";
  const bgHover=rappelMode?"#FEF3C7":T.blueLight;

  return(
    <div
      onMouseEnter={()=>setHovered(true)}
      onMouseLeave={()=>setHovered(false)}
      style={{display:"grid",gridTemplateColumns:"2fr 1.2fr 1.5fr 1.4fr 1fr 160px",gap:0,padding:"12px 16px",minWidth:700,borderBottom:isLast?"none":`1px solid ${rappelMode?"#FDE68A":T.border}`,alignItems:"center",background:hovered?bgHover:bgNormal,transition:"background .12s",cursor:"default"}}>

      {/* Nom */}
      <div style={{paddingRight:12}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{width:32,height:32,borderRadius:"50%",background:rappelMode?"#FDE68A":`linear-gradient(135deg,${T.blue},${T.blueDark})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:600,color:rappelMode?"#92400E":"#fff",flexShrink:0,border:rappelMode?"2px solid #F59E0B":"none"}}>
            {rappelMode?<i className="ti ti-phone-call" style={{fontSize:13}} aria-hidden/>:(lead.prenom||lead.nom||"?")[0].toUpperCase()}
          </div>
          <div>
            <p style={{fontSize:13,fontWeight:600,color:T.text,margin:0}}>{lead.prenom?lead.prenom+" ":""}{lead.nom}</p>
            {lead.adresse&&<p style={{fontSize:11,color:T.textHint,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:180}}>{lead.adresse}</p>}
          </div>
        </div>
        {lead.notes&&<p style={{fontSize:11,color:T.textHint,margin:"4px 0 0 40px",fontStyle:"italic",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:200}}>{lead.notes}</p>}
      </div>

      {/* Téléphone + WA */}
      <div style={{paddingRight:12}}>
        <p style={{fontSize:13,color:T.text,margin:"0 0 4px",display:"flex",alignItems:"center",gap:5}}>
          <i className="ti ti-phone" style={{fontSize:12,color:T.textHint}} aria-hidden/>
          {lead.telephone}
        </p>
        <a href={wa} target="_blank" rel="noreferrer" style={{background:"#DCFCE7",color:"#166534",borderRadius:99,padding:"2px 9px",fontSize:11,fontWeight:500,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:4}}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.554 4.122 1.522 5.852L.042 23.02a.999.999 0 0 0 1.233 1.233l5.168-1.48A11.956 11.956 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22a9.943 9.943 0 0 1-5.073-1.38l-.363-.216-3.765 1.077 1.077-3.765-.216-.363A9.943 9.943 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
          WhatsApp
        </a>
      </div>

      {/* Serrure */}
      <div style={{paddingRight:12}}>
        {serrure ? (
          <>
            <p style={{fontSize:13,color:T.text,margin:"0 0 2px",fontWeight:500}}>{serrure.nom}</p>
            <p style={{fontSize:12,color:"#7C3AED",margin:0,fontWeight:600}}>{serrure.prix} MAD</p>
          </>
        ) : <span style={{fontSize:12,color:T.textHint}}>—</span>}
      </div>

      {/* Rappel */}
      <div style={{paddingRight:12}}>
        {lead.rappelDate && lead.statut!=="gagne" && lead.statut!=="perdu" ? (
          <div style={{display:"inline-flex",alignItems:"center",gap:5,background:isRappelToday?"#FEF3C7":"#F8FAFC",border:`1px solid ${isRappelToday?"#FDE68A":T.border}`,borderRadius:T.radiusSm,padding:"4px 9px"}}>
            <i className="ti ti-calendar-event" style={{fontSize:12,color:isRappelToday?"#D97706":T.textHint}} aria-hidden/>
            <span style={{fontSize:12,color:isRappelToday?"#D97706":T.textSub,fontWeight:isRappelToday?500:400}}>
              {lead.rappelDate}{lead.rappelHeure&&` · ${lead.rappelHeure}`}
            </span>
          </div>
        ) : <span style={{fontSize:12,color:T.textHint}}>—</span>}
      </div>

      {/* Statut */}
      <div>
        <span style={{background:c.bg,color:c.text,border:`1px solid ${c.border}`,borderRadius:99,padding:"3px 10px",fontSize:11,fontWeight:500,display:"inline-flex",alignItems:"center",gap:5}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:c.dot,display:"inline-block"}}/>
          {c.label}
        </span>
      </div>

      {/* Actions */}
      <div style={{display:"flex",gap:5,alignItems:"center",justifyContent:"flex-end",flexWrap:"wrap"}}>
        <button onClick={onEdit} title="Modifier" style={{width:30,height:30,borderRadius:T.radiusSm,border:`1px solid ${T.border}`,background:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.textSub,transition:"all .12s"}}
          onMouseEnter={e=>{e.currentTarget.style.borderColor=T.blue;e.currentTarget.style.color=T.blue;}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.textSub;}}>
          <i className="ti ti-edit" style={{fontSize:14}} aria-hidden/>
        </button>
        {serrure&&<button onClick={()=>onSendSerrure(serrure)} title="Envoyer fiche serrure" style={{width:30,height:30,borderRadius:T.radiusSm,border:"1px solid #A7F3D0",background:"#ECFDF5",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#059669",transition:"all .12s"}}
          onMouseEnter={e=>e.currentTarget.style.background="#D1FAE5"}
          onMouseLeave={e=>e.currentTarget.style.background="#ECFDF5"}>
          <i className="ti ti-send" style={{fontSize:14}} aria-hidden/>
        </button>}
        {lead.statut!=="gagne"&&<button onClick={onCreateRdv} title="Créer un RDV" style={{height:30,borderRadius:T.radiusSm,border:"none",background:T.blue,color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",padding:"0 10px",gap:4,fontSize:12,fontWeight:500,transition:"background .12s"}}
          onMouseEnter={e=>e.currentTarget.style.background=T.blueDark}
          onMouseLeave={e=>e.currentTarget.style.background=T.blue}>
          <i className="ti ti-calendar-plus" style={{fontSize:13}} aria-hidden/>RDV
        </button>}
      </div>
    </div>
  );
}

/* ─── LEAD CARD (gardé pour compatibilité) ─── */
function LeadCard({lead,serrures,today,onEdit,onCreateRdv,onSendSerrure}){
  return <LeadRow lead={lead} serrures={serrures} today={today} onEdit={onEdit} onCreateRdv={onCreateRdv} onSendSerrure={onSendSerrure} idx={0} total={1}/>;
}

/* ─── SERRURE CATALOGUE CARD ─── */
function SerrureCatalogCard({serrure,leads,onSend}){
  const carac=serrure.caracteristiques?.filter(Boolean)||[];
  return(
    <div className="card" style={{overflow:"hidden",display:"flex",flexDirection:"column"}}>
      {/* Image */}
      <div style={{background:`linear-gradient(135deg,${T.blueLight},#E0F2FE)`,padding:"1.25rem",display:"flex",alignItems:"center",justifyContent:"center",minHeight:90,position:"relative"}}>
        {serrure.photos?.[0]
          ?<img src={serrure.photos[0]} alt={serrure.nom} style={{maxHeight:80,objectFit:"contain"}}/>
          :<i className="ti ti-lock" style={{fontSize:44,color:T.blue,opacity:.25}} aria-hidden/>
        }
        <span style={{position:"absolute",top:10,right:10,fontSize:10,background:"#fff",color:T.blue,border:`1px solid ${T.blueMid}`,borderRadius:99,padding:"2px 9px",fontWeight:600}}>{serrure.categorie}</span>
      </div>
      {/* Body */}
      <div style={{padding:"14px 16px",flex:1,display:"flex",flexDirection:"column",gap:8}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
          <p style={{fontSize:14,fontWeight:600,margin:0,color:T.text,lineHeight:1.3}}>{serrure.nom}</p>
          <span style={{fontSize:14,color:"#7C3AED",fontWeight:700,whiteSpace:"nowrap"}}>{serrure.prix} MAD</span>
        </div>
        <p style={{fontSize:12,color:T.textSub,margin:0,lineHeight:1.6}}>{serrure.description?.slice(0,90)}{serrure.description?.length>90?"…":""}</p>
        {carac.length>0&&(
          <div style={{display:"flex",flexDirection:"column",gap:3}}>
            {carac.slice(0,3).map((c,i)=>(
              <span key={i} style={{fontSize:11,color:T.textSub,display:"flex",alignItems:"center",gap:5}}>
                <i className="ti ti-check" style={{fontSize:12,color:"#059669",flexShrink:0}} aria-hidden/>{c}
              </span>
            ))}
          </div>
        )}
      </div>
      {/* Send button */}
      <div style={{padding:"0 16px 16px"}}>
        <ClientSelectButton serrure={serrure} leads={leads} onSend={onSend}/>
      </div>
    </div>
  );
}

/* ─── CLIENT SELECT BUTTON (inline dans la carte) ─── */
function ClientSelectButton({serrure,leads,onSend}){
  const [open,setOpen]=useState(false);
  const [search,setSearch]=useState("");
  const filtered=leads.filter(l=>!search||`${l.nom} ${l.prenom} ${l.telephone}`.toLowerCase().includes(search.toLowerCase()));
  const ref=useRef(null);

  useEffect(()=>{
    function handle(e){if(ref.current&&!ref.current.contains(e.target))setOpen(false);}
    document.addEventListener("mousedown",handle);
    return()=>document.removeEventListener("mousedown",handle);
  },[]);

  const lc=LEAD_COLORS;
  return(
    <div ref={ref} style={{position:"relative"}}>
      <button onClick={()=>setOpen(v=>!v)} style={{width:"100%",background:open?T.blueDark:T.blue,color:"#fff",border:"none",borderRadius:T.radiusSm,padding:"9px",fontSize:13,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:7,transition:"background .15s"}}>
        <i className="ti ti-send" style={{fontSize:15}} aria-hidden/> Envoyer à un client
      </button>
      {open&&(
        <div style={{position:"absolute",bottom:"calc(100% + 6px)",left:0,right:0,background:"#fff",border:`1px solid ${T.border}`,borderRadius:T.radius,boxShadow:T.shadowMd,zIndex:30,overflow:"hidden"}}>
          <div style={{padding:"10px 12px",borderBottom:`1px solid ${T.border}`}}>
            <p style={{fontSize:11,fontWeight:600,color:T.textSub,margin:"0 0 8px",textTransform:"uppercase",letterSpacing:".5px"}}>Choisir un client</p>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher…" style={{width:"100%",boxSizing:"border-box",fontSize:12,padding:"6px 10px"}} autoFocus/>
          </div>
          <div style={{maxHeight:220,overflowY:"auto"}}>
            {filtered.length===0?<p style={{fontSize:12,color:T.textHint,textAlign:"center",padding:"16px",margin:0}}>Aucun client</p>:
            filtered.map(l=>{
              const c=lc[l.statut]||lc.nouveau;
              const serrureL=serrure;
              return(
                <div key={l.id} onClick={()=>{onSend(l);setOpen(false);setSearch("");}}
                  style={{padding:"10px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:10,borderBottom:`1px solid ${T.border}`,transition:"background .1s"}}
                  onMouseEnter={e=>e.currentTarget.style.background=T.blueLight}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <div style={{width:32,height:32,borderRadius:"50%",background:`linear-gradient(135deg,${T.blue},${T.blueDark})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:600,color:"#fff",flexShrink:0}}>
                    {(l.prenom||l.nom||"?")[0].toUpperCase()}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontSize:13,fontWeight:500,color:T.text,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.prenom?l.prenom+" ":""}{l.nom}</p>
                    <p style={{fontSize:11,color:T.textSub,margin:0}}>{l.telephone}</p>
                  </div>
                  <span style={{fontSize:10,background:c.bg,color:c.text,border:`1px solid ${c.border}`,borderRadius:99,padding:"1px 7px",flexShrink:0}}>{c.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── CALENDAR ─── */
function CalendarView({days,calMonth,calYear,setCalMonth,setCalYear,getDayInterventions,selectedDate,setSelectedDate,user,users,onNew,onEdit}){
  const today=D(0);
  function prevMonth(){if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1);}
  function nextMonth(){if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1);}
  const selInts=selectedDate?getDayInterventions(selectedDate):[];
  return(
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"0.875rem",flexWrap:"wrap",gap:8}}>
        <h2 className="page-title" style={{margin:0}}>Calendrier</h2>
        {(user.role==="commercial"||user.role==="admin")&&<button onClick={()=>onNew(today)} className="btn-primary"><i className="ti ti-plus" style={{fontSize:15}} aria-hidden/> Nouveau RDV</button>}
      </div>
      <div style={{background:"var(--color-background-primary)",borderRadius:"var(--border-radius-lg)",border:"0.5px solid var(--color-border-tertiary)",overflow:"hidden",marginBottom:"1rem"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",borderBottom:`1px solid ${T.border}`}}>
          <button onClick={prevMonth} style={{background:"transparent",border:"0.5px solid var(--color-border-tertiary)",borderRadius:"var(--border-radius-md)",width:32,height:32,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><i className="ti ti-chevron-left" style={{fontSize:16}} aria-hidden/></button>
          <span style={{fontWeight:500,fontSize:15}}>{MONTHS[calMonth]} {calYear}</span>
          <button onClick={nextMonth} style={{background:"transparent",border:"0.5px solid var(--color-border-tertiary)",borderRadius:"var(--border-radius-md)",width:32,height:32,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><i className="ti ti-chevron-right" style={{fontSize:16}} aria-hidden/></button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)"}}>
          {DAYS_SHORT.map(d=><div key={d} style={{textAlign:"center",padding:"8px 0",fontSize:12,fontWeight:500,color:"var(--color-text-secondary)",borderBottom:`1px solid ${T.border}`}}>{d}</div>)}
          {days.map((day,idx)=>{
            if(!day) return <div key={`e${idx}`} style={{minHeight:52,borderBottom:`1px solid ${T.border}`,borderRight:idx%7!==6?"0.5px solid var(--color-border-tertiary)":"none"}}/>;
            const ints=getDayInterventions(day.dateStr);
            const isToday=day.dateStr===today;
            const isSel=day.dateStr===selectedDate;
            return(
              <div key={day.dateStr} onClick={()=>setSelectedDate(isSel?null:day.dateStr)} style={{minHeight:52,borderBottom:`1px solid ${T.border}`,borderRight:idx%7!==6?"0.5px solid var(--color-border-tertiary)":"none",padding:"4px 6px",cursor:"pointer",background:isSel?"#e0f2fe":"transparent",transition:"background .1s"}}>
                <span style={{fontSize:12,fontWeight:isToday?500:400,color:isToday?T.blue:"var(--color-text-primary)",background:isToday?T.blueLight:"transparent",borderRadius:"50%",width:22,height:22,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{day.d}</span>
                <div style={{marginTop:2,display:"flex",flexDirection:"column",gap:2}}>
                  {ints.slice(0,3).map(i=><div key={i.id} style={{background:COLORS[i.statut].border,borderRadius:3,height:4,width:"100%"}} title={i.clientName}/>)}
                  {ints.length>3&&<span style={{fontSize:10,color:"var(--color-text-secondary)"}}>+{ints.length-3}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {selectedDate&&(
        <div style={{background:"var(--color-background-primary)",borderRadius:"var(--border-radius-lg)",border:"0.5px solid var(--color-border-tertiary)",padding:"1rem"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <h3 style={{fontSize:14,fontWeight:500,margin:0}}>{new Date(selectedDate+"T12:00:00").toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})}</h3>
            {(user.role==="commercial"||user.role==="admin")&&<button onClick={()=>onNew(selectedDate)} style={{background:"transparent",border:"0.5px solid var(--color-border-secondary)",borderRadius:"var(--border-radius-md)",padding:"4px 10px",fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}><i className="ti ti-plus" style={{fontSize:14}} aria-hidden/> Ajouter</button>}
          </div>
          {selInts.length===0?<Empty text="Aucune intervention ce jour"/>:<div style={{display:"flex",flexDirection:"column",gap:8}}>{selInts.sort((a,b)=>a.heure.localeCompare(b.heure)).map(i=><DetailCard key={i.id} i={i} users={users} user={user} onEdit={()=>onEdit(i)}/>)}</div>}
        </div>
      )}
      <Legend/>
    </div>
  );
}

/* ─── LISTE VIEW ─── */
function ListeView({interventions,user,users,onEdit,onNew,onDelete}){
  const [filter,setFilter]=useState("tous");
  const [search,setSearch]=useState("");
  const filtered=interventions.filter(i=>filter==="tous"||i.statut===filter).filter(i=>!search||i.clientName.toLowerCase().includes(search.toLowerCase())||i.type.toLowerCase().includes(search.toLowerCase())).sort((a,b)=>b.date.localeCompare(a.date));
  return(
    <div>
      <div className="page-header">
        <h2 className="page-title" style={{margin:0}}>Interventions</h2>
        {(user.role==="commercial"||user.role==="admin")&&<button onClick={onNew} className="btn-primary"><i className="ti ti-plus" style={{fontSize:15}} aria-hidden/> Nouveau RDV</button>}
      </div>
      <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher..." style={{flex:1,minWidth:0,width:"100%"}}/>
        {["tous","nouveau","encours","fait","annule"].map(s=>(
          <button key={s} onClick={()=>setFilter(s)} style={{background:filter===s?"#4DA6E8":"var(--color-background-primary)",color:filter===s?"white":"var(--color-text-secondary)",border:filter===s?"none":"0.5px solid var(--color-border-secondary)",borderRadius:99,padding:"5px 12px",fontSize:12,cursor:"pointer",fontWeight:filter===s?500:400}}>
            {s==="tous"?"Tous":COLORS[s].label}
          </button>
        ))}
      </div>
      {filtered.length===0?<div style={{textAlign:"center",padding:"2rem",color:"var(--color-text-tertiary)"}}><i className="ti ti-search" style={{fontSize:32,display:"block",marginBottom:8}} aria-hidden/>Aucune intervention trouvée</div>:(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>{filtered.map(i=><DetailCard key={i.id} i={i} users={users} user={user} onEdit={()=>onEdit(i)}/>)}</div>
      )}
    </div>
  );
}

/* ─── NOTIF PAGE ─── */
function NotifPage({notifs,onRead,onReadAll,interventions,leads}){
  const sorted=[...notifs].sort((a,b)=>new Date(b.date)-new Date(a.date));
  const unread=notifs.filter(n=>!n.lu).length;
  return(
    <div>
      <div className="page-header">
        <h2 className="page-title" style={{margin:0}}>Notifications {unread>0&&<span style={{fontSize:13,color:"#4DA6E8",fontWeight:400}}>({unread} non lues)</span>}</h2>
        {unread>0&&<button onClick={onReadAll} style={{fontSize:12,color:"var(--color-text-secondary)",background:"transparent",border:"0.5px solid var(--color-border-secondary)",borderRadius:"var(--border-radius-md)",padding:"5px 10px",cursor:"pointer"}}>Tout marquer lu</button>}
      </div>
      {sorted.length===0?<div style={{textAlign:"center",padding:"3rem",color:"var(--color-text-tertiary)"}}><i className="ti ti-bell-off" style={{fontSize:40,display:"block",marginBottom:8}} aria-hidden/>Aucune notification</div>:(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {sorted.map(n=>{
            const int=interventions.find(i=>i.id===n.interventionId);
            const lead=leads.find(l=>l.id===n.leadId);
            const isRappel=n.message.toLowerCase().includes("rappel");
            return(
              <div key={n.id} onClick={()=>onRead(n.id)} style={{background:n.lu?"var(--color-background-primary)":isRappel?"#fff7ed":"#e0f2fe",borderRadius:"var(--border-radius-lg)",border:n.lu?"0.5px solid var(--color-border-tertiary)":isRappel?"0.5px solid #fb923c":"0.5px solid #38bdf8",padding:"12px 16px",cursor:"pointer"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <div style={{flex:1}}>
                    <p style={{fontSize:14,margin:0,fontWeight:n.lu?400:500,color:"var(--color-text-primary)"}}>{n.message}</p>
                    {int&&<div style={{marginTop:6,display:"flex",gap:6,flexWrap:"wrap",marginTop:4}}><StatusBadge statut={int.statut} small/>{int.prix&&<PrixBadge prix={int.prix}/>}</div>}
                    {lead&&<div style={{marginTop:6}}><span style={{fontSize:12,color:"#c2410c"}}><i className="ti ti-user" style={{fontSize:12,verticalAlign:-2}} aria-hidden/> {lead.prenom?lead.prenom+" ":""}{lead.nom}</span></div>}
                  </div>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
                    <span style={{fontSize:11,color:"var(--color-text-tertiary)",whiteSpace:"nowrap"}}>{new Date(n.date).toLocaleDateString("fr-FR")}</span>
                    {!n.lu&&<span style={{width:8,height:8,borderRadius:"50%",background:isRappel?"#fb923c":"#4DA6E8",display:"block"}}/>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── DETAIL CARD ─── */
function DetailCard({i,users,user,onEdit}){
  const c=COLORS[i.statut];
  const tech=users.find(u=>u.id===i.technicienId);
  const locked=user.role==="technicien"&&i.statut==="fait"&&!!i.signature;
  return(
    <div className="card" style={{padding:"14px 18px",borderLeft:`3px solid ${c.dot}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:6}}>
            <p style={{fontSize:14,fontWeight:600,color:T.text,margin:0}}>{i.clientName}</p>
            <StatusBadge statut={i.statut} small/>
            {i.prix&&<PrixBadge prix={i.prix}/>}
            {locked&&<span style={{fontSize:11,color:"#059669",background:"#ECFDF5",border:"1px solid #A7F3D0",borderRadius:99,padding:"2px 9px",display:"inline-flex",alignItems:"center",gap:4}}><i className="ti ti-lock" style={{fontSize:11}} aria-hidden/>Validé</span>}
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:"4px 16px"}}>
            <span style={{fontSize:12,color:T.textSub,display:"inline-flex",alignItems:"center",gap:4}}><i className="ti ti-clock" style={{fontSize:12,color:T.textHint}} aria-hidden/>{i.heure} · {i.type}</span>
            <span style={{fontSize:12,color:T.textSub,display:"inline-flex",alignItems:"center",gap:4}}><i className="ti ti-map-pin" style={{fontSize:12,color:T.textHint}} aria-hidden/>{i.clientAddress}</span>
            {i.clientPhone&&<span style={{fontSize:12,color:T.textSub,display:"inline-flex",alignItems:"center",gap:4}}><i className="ti ti-phone" style={{fontSize:12,color:T.textHint}} aria-hidden/>{i.clientPhone}</span>}
            {tech&&<span style={{fontSize:12,color:T.textSub,display:"inline-flex",alignItems:"center",gap:4}}><i className="ti ti-user" style={{fontSize:12,color:T.textHint}} aria-hidden/>{tech.name}</span>}
          </div>
          {i.notes&&<p style={{fontSize:12,color:T.textHint,margin:"6px 0 0",fontStyle:"italic"}}>{i.notes}</p>}
        </div>
        {locked?(
          <div style={{background:"#ECFDF5",border:"1px solid #A7F3D0",borderRadius:T.radiusSm,padding:"6px 12px",fontSize:12,color:"#059669",display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
            <i className="ti ti-lock" style={{fontSize:13}} aria-hidden/>Verrouillé
          </div>
        ):(
          <button onClick={onEdit} className="btn-ghost" style={{flexShrink:0,padding:"6px 14px"}}>
            <i className="ti ti-edit" style={{fontSize:13}} aria-hidden/>
            {user.role==="technicien"?"Statuer":"Modifier"}
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── SMALL COMPONENTS ─── */
function Section({title,count,color,children}){return(<div className="card" style={{padding:"1.25rem"}}><div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"0.875rem",flexWrap:"wrap",gap:8}}><h3 style={{fontSize:13,fontWeight:600,margin:0,color:T.text,textTransform:"uppercase",letterSpacing:".5px"}}>{title}</h3>{count!==undefined&&<span style={{fontSize:12,fontWeight:600,color:color||T.blue,background:T.blueLight,borderRadius:99,padding:"2px 9px"}}>{count}</span>}</div><div style={{display:"flex",flexDirection:"column",gap:8}}>{children}</div></div>);}
function Empty({text}){return<div style={{textAlign:"center",padding:"1.5rem 1rem",color:T.textHint}}><i className="ti ti-inbox" style={{fontSize:28,display:"block",marginBottom:6}} aria-hidden/><p style={{fontSize:13,margin:0}}>{text}</p></div>;}
function IntCard({i,users,onClick}){const tech=users.find(u=>u.id===i.technicienId);return(<div onClick={onClick} style={{background:T.pageBg,borderRadius:T.radiusSm,padding:"11px 14px",border:`1px solid ${T.border}`,cursor:"pointer",transition:"all .15s"}} onMouseEnter={e=>{e.currentTarget.style.borderColor=T.blue;e.currentTarget.style.background=T.blueLight;}} onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.background=T.pageBg;}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}><p style={{fontSize:13,fontWeight:500,color:T.text,margin:0}}>{i.clientName}</p><StatusBadge statut={i.statut} small/></div><p style={{fontSize:12,color:T.textSub,margin:"2px 0"}}>{i.heure} · {i.type}</p>{i.prix&&<p style={{fontSize:12,color:"#7C3AED",margin:"2px 0",fontWeight:500}}>{i.prix} MAD</p>}{tech&&<p style={{fontSize:11,color:T.textHint,margin:"2px 0"}}>Tech: {tech.name}</p>}</div>);}
function StatusBadge({statut,small}){const c=COLORS[statut];return(<span style={{background:c.bg,color:c.text,border:`1px solid ${c.border}`,borderRadius:99,padding:small?"2px 9px":"3px 11px",fontSize:small?11:12,fontWeight:500,whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:5}}><span style={{width:6,height:6,borderRadius:"50%",background:c.dot,flexShrink:0,display:"inline-block"}}/>{c.label}</span>);}
function PrixBadge({prix}){if(!prix)return null;return(<span style={{background:"#F5F3FF",color:"#7C3AED",border:"1px solid #DDD6FE",borderRadius:99,padding:"2px 10px",fontSize:12,fontWeight:500,whiteSpace:"nowrap"}}>{prix} MAD</span>);}
function Legend(){return(<div className="card" style={{display:"flex",gap:16,flexWrap:"wrap",marginTop:"1rem",padding:"12px 16px",alignItems:"center"}}><span style={{fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:".5px"}}>Legende:</span>{Object.entries(COLORS).map(([key,c])=>(<div key={key} style={{display:"flex",alignItems:"center",gap:6}}><span style={{width:8,height:8,borderRadius:"50%",background:c.dot,display:"inline-block"}}/><span style={{fontSize:12,color:T.textSub}}>{c.label}</span></div>))}</div>);}
function Field({label,children}){return(<div><label style={{fontSize:11,fontWeight:600,color:T.textSub,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:".5px"}}>{label}</label>{children}</div>);}

/* ─── LEAD MODAL ─── */
function LeadModal({lead,serrures,onSave,onClose,onDelete}){
  const [form,setForm]=useState({id:lead?.id||null,nom:lead?.nom||"",prenom:lead?.prenom||"",telephone:lead?.telephone||"",adresse:lead?.adresse||"",serrureId:lead?.serrureId||serrures[0]?.id||null,statut:lead?.statut||"nouveau",notes:lead?.notes||"",rappelDate:lead?.rappelDate||"",rappelHeure:lead?.rappelHeure||""});
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const serrureSelectionnee=serrures.find(s=>s.id===Number(form.serrureId));
  return(
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box" onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 20px",borderBottom:`1px solid ${T.border}`}}>
          <h3 style={{margin:0,fontSize:16,fontWeight:600,color:T.text}}>{lead?.id?"Modifier le client":"Nouveau client"}</h3>
          <button onClick={onClose} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:18,color:"var(--color-text-secondary)"}}><i className="ti ti-x" aria-hidden/></button>
        </div>
        <div style={{padding:"20px",display:"flex",flexDirection:"column",gap:14}}>
          <div className="grid-2">
            <Field label="Prénom"><input value={form.prenom} onChange={e=>set("prenom",e.target.value)} placeholder="Prénom" style={{width:"100%",boxSizing:"border-box"}}/></Field>
            <Field label="Nom *"><input value={form.nom} onChange={e=>set("nom",e.target.value)} placeholder="Nom" style={{width:"100%",boxSizing:"border-box"}}/></Field>
          </div>
          <Field label="Téléphone *">
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <input value={form.telephone} onChange={e=>set("telephone",e.target.value)} placeholder="0600000000" style={{flex:1,boxSizing:"border-box"}}/>
              {form.telephone&&<a href={`https://wa.me/212${form.telephone.replace(/^0/,"").replace(/\s/g,"")}`} target="_blank" rel="noreferrer" style={{background:"#25D366",color:"white",borderRadius:"var(--border-radius-md)",padding:"7px 12px",fontSize:12,fontWeight:500,textDecoration:"none",display:"flex",alignItems:"center",gap:4,whiteSpace:"nowrap"}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.554 4.122 1.522 5.852L.042 23.02a.999.999 0 0 0 1.233 1.233l5.168-1.48A11.956 11.956 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22a9.943 9.943 0 0 1-5.073-1.38l-.363-.216-3.765 1.077 1.077-3.765-.216-.363A9.943 9.943 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
                WhatsApp
              </a>}
            </div>
          </Field>
          <Field label="Adresse"><input value={form.adresse} onChange={e=>set("adresse",e.target.value)} placeholder="Adresse du client" style={{width:"100%",boxSizing:"border-box"}}/></Field>
          <Field label="Serrure souhaitée">
            <select value={form.serrureId||""} onChange={e=>set("serrureId",Number(e.target.value))} style={{width:"100%",boxSizing:"border-box"}}>
              <option value="">— Choisir une serrure —</option>
              {serrures.map(s=><option key={s.id} value={s.id}>{s.nom} — {s.prix} MAD</option>)}
            </select>
            {serrureSelectionnee&&<div style={{marginTop:6,background:"#f5f3ff",border:"0.5px solid #c4b5fd",borderRadius:"var(--border-radius-md)",padding:"8px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:12,color:"var(--color-text-secondary)"}}>{serrureSelectionnee.categorie} · {serrureSelectionnee.description?.slice(0,50)}…</span>
              <span style={{fontSize:13,color:"#7c3aed",fontWeight:500,marginLeft:8}}>{serrureSelectionnee.prix} MAD</span>
            </div>}
          </Field>
          <Field label="Statut">
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
              {Object.entries(LEAD_COLORS).map(([key,c])=>(
                <div key={key} onClick={()=>set("statut",key)} style={{padding:"8px 6px",borderRadius:"var(--border-radius-md)",border:form.statut===key?`2px solid ${c.border}`:`0.5px solid ${c.border}`,background:form.statut===key?c.bg:"transparent",cursor:"pointer",textAlign:"center"}}>
                  <span style={{fontSize:12,color:c.text,fontWeight:form.statut===key?500:400}}>{c.label}</span>
                </div>
              ))}
            </div>
          </Field>
          <div style={{background:"#fff7ed",border:"0.5px solid #fb923c",borderRadius:"var(--border-radius-md)",padding:"12px 14px"}}>
            <p style={{fontSize:12,fontWeight:500,color:"#c2410c",margin:"0 0 8px",display:"flex",alignItems:"center",gap:5}}><i className="ti ti-alarm" style={{fontSize:14}} aria-hidden/>Programmation rappel</p>
            <div className="grid-2">
              <Field label="Date de rappel"><input type="date" value={form.rappelDate} onChange={e=>set("rappelDate",e.target.value)} style={{width:"100%",boxSizing:"border-box"}}/></Field>
              <Field label="Heure"><input type="time" value={form.rappelHeure} onChange={e=>set("rappelHeure",e.target.value)} style={{width:"100%",boxSizing:"border-box"}}/></Field>
            </div>
          </div>
          <Field label="Notes"><textarea value={form.notes} onChange={e=>set("notes",e.target.value)} placeholder="Remarques, historique des échanges..." rows={3} style={{width:"100%",boxSizing:"border-box",resize:"vertical"}}/></Field>
        </div>
        <div style={{display:"flex",gap:8,padding:"14px 16px",borderTop:`1px solid ${T.border}`,justifyContent:"space-between",flexWrap:"wrap"}}>
          <div>{onDelete&&lead?.id&&<button onClick={()=>onDelete(lead.id)} style={{background:"#fef2f2",color:"#dc2626",border:"0.5px solid #fca5a5",borderRadius:"var(--border-radius-md)",padding:"8px 14px",fontSize:13,cursor:"pointer"}}><i className="ti ti-trash" aria-hidden/> Supprimer</button>}</div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={onClose} style={{background:"transparent",border:"0.5px solid var(--color-border-secondary)",borderRadius:"var(--border-radius-md)",padding:"8px 14px",fontSize:13,cursor:"pointer"}}>Annuler</button>
            <button onClick={()=>form.nom&&onSave(form)} style={{background:form.nom?"#4DA6E8":"#94a3b8",color:"white",border:"none",borderRadius:"var(--border-radius-md)",padding:"8px 16px",fontSize:13,fontWeight:500,cursor:form.nom?"pointer":"not-allowed"}}>{lead?.id?"Enregistrer":"Ajouter client"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── SERRURE MODAL (gestion catalogue) ─── */
function SerrureModal({serrures,onSave,onDelete,onClose,onSendSerrure,leads}){
  const [editS,setEditS]=useState(null);
  if(editS!==null){
    const s=editS==="new"?{nom:"",prix:"",categorie:"Connectée",description:"",caracteristiques:[],photos:[],photosInstallation:[]}:editS;
    return <SerrureEditForm serrure={s} onSave={data=>{onSave(data);setEditS(null);}} onBack={()=>setEditS(null)} onDelete={s.id?()=>{onDelete(s.id);setEditS(null);}:null}/>;
  }
  return(
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box" onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 20px",borderBottom:`1px solid ${T.border}`}}>
          <h3 style={{margin:0,fontSize:16,fontWeight:600,color:T.text}}>Gestion des serrures</h3>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setEditS("new")} style={{background:"#4DA6E8",color:"white",border:"none",borderRadius:"var(--border-radius-md)",padding:"6px 12px",fontSize:12,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}><i className="ti ti-plus" aria-hidden/> Ajouter</button>
            <button onClick={onClose} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:18,color:"var(--color-text-secondary)"}}><i className="ti ti-x" aria-hidden/></button>
          </div>
        </div>
        <div style={{padding:"1rem 16px",display:"flex",flexDirection:"column",gap:10}}>
          {serrures.map(s=>(
            <div key={s.id} style={{background:"var(--color-background-secondary)",borderRadius:"var(--border-radius-lg)",padding:"12px 14px",display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:48,height:48,borderRadius:"var(--border-radius-md)",background:"#e0f2fe",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
                {s.photos?.[0]?<img src={s.photos[0]} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<i className="ti ti-lock" style={{fontSize:24,color:"#4DA6E8"}} aria-hidden/>}
              </div>
              <div style={{flex:1}}>
                <p style={{fontSize:13,fontWeight:500,margin:"0 0 2px"}}>{s.nom}</p>
                <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                  <span style={{fontSize:11,background:"#e0f2fe",color:"#0369a1",borderRadius:99,padding:"1px 8px"}}>{s.categorie}</span>
                  <span style={{fontSize:12,color:"#7c3aed",fontWeight:500}}>{s.prix} MAD</span>
                </div>
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0}}>
                <button onClick={()=>setEditS(s)} style={{background:"var(--color-background-primary)",border:"0.5px solid var(--color-border-secondary)",borderRadius:"var(--border-radius-md)",padding:"5px 10px",fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:3}}><i className="ti ti-edit" style={{fontSize:13}} aria-hidden/> Modifier</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── SERRURE EDIT FORM ─── */
function SerrureEditForm({serrure,onSave,onBack,onDelete}){
  const [form,setForm]=useState({...serrure,caracteristiques:[...(serrure.caracteristiques||[])],photos:[...(serrure.photos||[])],photosInstallation:[...(serrure.photosInstallation||[])]});
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  function addCarac(){set("caracteristiques",[...form.caracteristiques,""]);}
  function setCarac(i,v){const a=[...form.caracteristiques];a[i]=v;set("caracteristiques",a);}
  function removeCarac(i){set("caracteristiques",form.caracteristiques.filter((_,idx)=>idx!==i));}
  function handlePhoto(e,field){
    const files=Array.from(e.target.files);
    Promise.all(files.map(f=>new Promise(res=>{const r=new FileReader();r.onload=ev=>res(ev.target.result);r.readAsDataURL(f);}))).then(imgs=>set(field,[...form[field],...imgs].slice(0,6)));
  }
  return(
    <div className="modal-overlay">
      <div className="modal-box">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 20px",borderBottom:`1px solid ${T.border}`}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <button onClick={onBack} style={{background:"transparent",border:"0.5px solid var(--color-border-secondary)",borderRadius:"var(--border-radius-md)",padding:"4px 8px",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",gap:3}}><i className="ti ti-arrow-left" style={{fontSize:13}} aria-hidden/> Retour</button>
            <h3 style={{margin:0,fontSize:16,fontWeight:600,color:T.text}}>{serrure.id?"Modifier la serrure":"Nouvelle serrure"}</h3>
          </div>
          <button onClick={onBack} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:18,color:"var(--color-text-secondary)"}}><i className="ti ti-x" aria-hidden/></button>
        </div>
        <div style={{padding:"20px",display:"flex",flexDirection:"column",gap:14}}>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:10}}>
            <Field label="Nom de la serrure *"><input value={form.nom} onChange={e=>set("nom",e.target.value)} placeholder="Ex: Yale Linus Smart Lock" style={{width:"100%",boxSizing:"border-box"}}/></Field>
            <Field label="Prix (MAD) *">
              <div style={{position:"relative"}}>
                <input value={form.prix} onChange={e=>set("prix",e.target.value)} placeholder="1 200" style={{width:"100%",boxSizing:"border-box",paddingRight:40,border:"0.5px solid #c4b5fd",borderRadius:"var(--border-radius-md)"}}/>
                <span style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",fontSize:11,color:"#7c3aed",pointerEvents:"none"}}>MAD</span>
              </div>
            </Field>
          </div>
          <div className="grid-2">
            <Field label="Catégorie">
              <select value={form.categorie} onChange={e=>set("categorie",e.target.value)} style={{width:"100%",boxSizing:"border-box"}}>
                {["Connectée","Biométrique","RFID","Code PIN","Autre"].map(c=><option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description"><textarea value={form.description} onChange={e=>set("description",e.target.value)} placeholder="Description commerciale de la serrure..." rows={3} style={{width:"100%",boxSizing:"border-box",resize:"vertical"}}/></Field>
          <div>
            <label style={{fontSize:12,color:"var(--color-text-secondary)",display:"block",marginBottom:4}}>Caractéristiques</label>
            {form.caracteristiques.map((c,i)=>(
              <div key={i} style={{display:"flex",gap:6,marginBottom:6}}>
                <input value={c} onChange={e=>setCarac(i,e.target.value)} placeholder={`Caractéristique ${i+1}`} style={{flex:1,boxSizing:"border-box"}}/>
                <button onClick={()=>removeCarac(i)} style={{background:"transparent",border:"0.5px solid #fca5a5",borderRadius:"var(--border-radius-md)",padding:"0 8px",cursor:"pointer",color:"#dc2626"}}><i className="ti ti-x" style={{fontSize:13}} aria-hidden/></button>
              </div>
            ))}
            <button onClick={addCarac} style={{background:"transparent",border:"0.5px dashed var(--color-border-secondary)",borderRadius:"var(--border-radius-md)",padding:"6px 12px",fontSize:12,cursor:"pointer",color:"var(--color-text-secondary)",display:"flex",alignItems:"center",gap:4}}><i className="ti ti-plus" style={{fontSize:13}} aria-hidden/> Ajouter une caractéristique</button>
          </div>
          <Field label="Photos du produit (max 6)">
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
              {form.photos.map((p,i)=>(
                <div key={i} style={{position:"relative",borderRadius:"var(--border-radius-md)",overflow:"hidden",aspectRatio:"1",background:"#f1f5f9"}}>
                  <img src={p} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  <button onClick={()=>set("photos",form.photos.filter((_,idx)=>idx!==i))} style={{position:"absolute",top:3,right:3,width:20,height:20,borderRadius:"50%",background:"rgba(0,0,0,.55)",border:"none",color:"white",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11}}><i className="ti ti-x" aria-hidden/></button>
                </div>
              ))}
              {form.photos.length<6&&<label style={{borderRadius:"var(--border-radius-md)",border:"1.5px dashed #94a3b8",background:"#f8fafc",aspectRatio:"1",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",gap:2}}>
                <i className="ti ti-photo-plus" style={{fontSize:20,color:"#94a3b8"}} aria-hidden/>
                <span style={{fontSize:10,color:"#94a3b8"}}>Ajouter</span>
                <input type="file" accept="image/*" multiple onChange={e=>handlePhoto(e,"photos")} style={{display:"none"}}/>
              </label>}
            </div>
          </Field>
          <Field label="Photos d'installation terrain (max 6)">
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
              {form.photosInstallation.map((p,i)=>(
                <div key={i} style={{position:"relative",borderRadius:"var(--border-radius-md)",overflow:"hidden",aspectRatio:"1",background:"#f1f5f9"}}>
                  <img src={p} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  <button onClick={()=>set("photosInstallation",form.photosInstallation.filter((_,idx)=>idx!==i))} style={{position:"absolute",top:3,right:3,width:20,height:20,borderRadius:"50%",background:"rgba(0,0,0,.55)",border:"none",color:"white",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11}}><i className="ti ti-x" aria-hidden/></button>
                </div>
              ))}
              {form.photosInstallation.length<6&&<label style={{borderRadius:"var(--border-radius-md)",border:"1.5px dashed #94a3b8",background:"#f8fafc",aspectRatio:"1",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",gap:2}}>
                <i className="ti ti-camera" style={{fontSize:20,color:"#94a3b8"}} aria-hidden/>
                <span style={{fontSize:10,color:"#94a3b8"}}>Terrain</span>
                <input type="file" accept="image/*" multiple onChange={e=>handlePhoto(e,"photosInstallation")} style={{display:"none"}}/>
              </label>}
            </div>
          </Field>
        </div>
        <div style={{display:"flex",gap:8,padding:"14px 16px",borderTop:`1px solid ${T.border}`,justifyContent:"space-between",flexWrap:"wrap"}}>
          <div>{onDelete&&<button onClick={onDelete} style={{background:"#fef2f2",color:"#dc2626",border:"0.5px solid #fca5a5",borderRadius:"var(--border-radius-md)",padding:"8px 14px",fontSize:13,cursor:"pointer"}}><i className="ti ti-trash" aria-hidden/> Supprimer</button>}</div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={onBack} style={{background:"transparent",border:"0.5px solid var(--color-border-secondary)",borderRadius:"var(--border-radius-md)",padding:"8px 14px",fontSize:13,cursor:"pointer"}}>Annuler</button>
            <button onClick={()=>form.nom&&form.prix&&onSave(form)} style={{background:form.nom&&form.prix?"#4DA6E8":"#94a3b8",color:"white",border:"none",borderRadius:"var(--border-radius-md)",padding:"8px 16px",fontSize:13,fontWeight:500,cursor:form.nom&&form.prix?"pointer":"not-allowed"}}>{serrure.id?"Enregistrer":"Créer la serrure"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── SEND SERRURE MODAL ─── */
function SendSerrureModal({serrure,lead,leads,onClose}){
  const [selectedLead,setSelectedLead]=useState(lead||null);
  if(!serrure) return null;
  const carac=serrure.caracteristiques?.filter(Boolean)||[];
  const message=`Bonjour${selectedLead?` ${selectedLead.prenom||""} ${selectedLead.nom}`:""} 👋\n\nJe vous présente notre solution *${serrure.nom}* :\n\n🔐 *${serrure.nom}* — Catégorie : ${serrure.categorie}\n💰 Prix : *${serrure.prix} MAD*\n\n📝 ${serrure.description}${carac.length?"\n\n✅ Caractéristiques :\n"+carac.map(c=>`• ${c}`).join("\n"):""}\n\nNous pouvons planifier une installation à votre convenance. N'hésitez pas à nous contacter !\n\n_Réparation WiFi — Votre spécialiste serrures digitales_`;
  const phone=selectedLead?.telephone?.replace(/^0/,"").replace(/\s/g,"");
  const waUrl=phone?`https://wa.me/212${phone}?text=${encodeURIComponent(message)}`:`https://wa.me/?text=${encodeURIComponent(message)}`;
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(241,245,249,0.85)",backdropFilter:"blur(4px)",zIndex:110,display:"flex",alignItems:"center",justifyContent:"center",padding:"1.5rem"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box" onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 20px",borderBottom:`1px solid ${T.border}`}}>
          <h3 style={{margin:0,fontSize:16,fontWeight:600,color:T.text}}>Envoyer la fiche serrure</h3>
          <button onClick={onClose} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:18,color:"var(--color-text-secondary)"}}><i className="ti ti-x" aria-hidden/></button>
        </div>
        <div style={{padding:"14px",display:"flex",flexDirection:"column",gap:13}}>
          {/* Serrure preview */}
          <div style={{background:"var(--color-background-secondary)",borderRadius:"var(--border-radius-lg)",padding:"14px",display:"flex",gap:12,alignItems:"flex-start"}}>
            <div style={{width:56,height:56,borderRadius:"var(--border-radius-md)",background:"#e0f2fe",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
              {serrure.photos?.[0]?<img src={serrure.photos[0]} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<i className="ti ti-lock" style={{fontSize:28,color:"#4DA6E8"}} aria-hidden/>}
            </div>
            <div style={{flex:1}}>
              <p style={{fontSize:14,fontWeight:500,margin:"0 0 2px"}}>{serrure.nom}</p>
              <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:4}}>
                <span style={{fontSize:11,background:"#e0f2fe",color:"#0369a1",borderRadius:99,padding:"1px 8px"}}>{serrure.categorie}</span>
                <span style={{fontSize:13,color:"#7c3aed",fontWeight:500}}>{serrure.prix} MAD</span>
              </div>
              <p style={{fontSize:12,color:"var(--color-text-secondary)",margin:0,lineHeight:1.5}}>{serrure.description?.slice(0,80)}…</p>
            </div>
          </div>

          {/* Sélection client */}
          <Field label="Envoyer à quel client ?">
            <select value={selectedLead?.id||""} onChange={e=>{const l=leads.find(x=>x.id===Number(e.target.value));setSelectedLead(l||null);}} style={{width:"100%",boxSizing:"border-box"}}>
              <option value="">— Sélectionner un client —</option>
              {leads.map(l=><option key={l.id} value={l.id}>{l.prenom?l.prenom+" ":""}{l.nom} ({l.telephone})</option>)}
            </select>
          </Field>

          {/* Aperçu message */}
          <Field label="Aperçu du message WhatsApp">
            <div style={{background:"#f0fdf4",border:"0.5px solid #4ade80",borderRadius:"var(--border-radius-md)",padding:"12px",fontSize:12,color:"var(--color-text-primary)",lineHeight:1.7,maxHeight:150,overflowY:"auto",whiteSpace:"pre-wrap"}}>
              {message}
            </div>
          </Field>

          {serrure.photosInstallation?.length>0&&(
            <Field label={`Photos terrain (${serrure.photosInstallation.length})`}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}}>
                {serrure.photosInstallation.map((p,i)=><div key={i} style={{borderRadius:"var(--border-radius-md)",overflow:"hidden",aspectRatio:"1"}}><img src={p} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/></div>)}
              </div>
              <p style={{fontSize:11,color:"var(--color-text-tertiary)",margin:"4px 0 0"}}>Les photos seront à partager manuellement dans WhatsApp après l'envoi du message.</p>
            </Field>
          )}
        </div>
        <div style={{padding:"16px 20px",borderTop:`1px solid ${T.border}`,display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={onClose} style={{background:"transparent",border:"0.5px solid var(--color-border-secondary)",borderRadius:"var(--border-radius-md)",padding:"8px 14px",fontSize:13,cursor:"pointer"}}>Annuler</button>
          <a href={waUrl} target="_blank" rel="noreferrer" onClick={onClose} style={{background:"#25D366",color:"white",borderRadius:"var(--border-radius-md)",padding:"8px 16px",fontSize:13,fontWeight:500,textDecoration:"none",display:"flex",alignItems:"center",gap:6}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.554 4.122 1.522 5.852L.042 23.02a.999.999 0 0 0 1.233 1.233l5.168-1.48A11.956 11.956 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22a9.943 9.943 0 0 1-5.073-1.38l-.363-.216-3.765 1.077 1.077-3.765-.216-.363A9.943 9.943 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
            Ouvrir WhatsApp Business
          </a>
        </div>
      </div>
    </div>
  );
}

/* ─── INTERVENTION MODAL ─── */
function SignaturePad({value,onChange}){
  const canvasRef=useRef(null);const drawing=useRef(false);const lastPos=useRef(null);
  function getPos(e,canvas){const rect=canvas.getBoundingClientRect();const src=e.touches?e.touches[0]:e;return{x:(src.clientX-rect.left)*(canvas.width/rect.width),y:(src.clientY-rect.top)*(canvas.height/rect.height)};}
  function start(e){e.preventDefault();drawing.current=true;lastPos.current=getPos(e,canvasRef.current);}
  function move(e){e.preventDefault();if(!drawing.current)return;const canvas=canvasRef.current;const ctx=canvas.getContext("2d");const pos=getPos(e,canvas);ctx.beginPath();ctx.moveTo(lastPos.current.x,lastPos.current.y);ctx.lineTo(pos.x,pos.y);ctx.strokeStyle="#1e293b";ctx.lineWidth=2.5;ctx.lineCap="round";ctx.lineJoin="round";ctx.stroke();lastPos.current=pos;onChange(canvas.toDataURL());}
  function stop(e){e.preventDefault();drawing.current=false;lastPos.current=null;}
  function clear(){const canvas=canvasRef.current;canvas.getContext("2d").clearRect(0,0,canvas.width,canvas.height);onChange(null);}
  useEffect(()=>{if(!value&&canvasRef.current)canvasRef.current.getContext("2d").clearRect(0,0,canvasRef.current.width,canvasRef.current.height);},[value]);
  return(
    <div>
      <div style={{position:"relative",border:"1.5px dashed #94a3b8",borderRadius:"var(--border-radius-md)",background:"#f8fafc",overflow:"hidden",touchAction:"none"}}>
        <canvas ref={canvasRef} width={440} height={160} style={{width:"100%",height:160,display:"block",cursor:"crosshair",touchAction:"none"}} onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop} onTouchStart={start} onTouchMove={move} onTouchEnd={stop}/>
        {!value&&<div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}><p style={{fontSize:13,color:"#94a3b8",margin:0}}>Le client signe ici avec son doigt</p></div>}
      </div>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:6}}>
        <button onClick={clear} style={{fontSize:12,color:"#64748b",background:"transparent",border:"0.5px solid var(--color-border-secondary)",borderRadius:"var(--border-radius-md)",padding:"4px 10px",cursor:"pointer",display:"flex",alignItems:"center",gap:4}}><i className="ti ti-refresh" style={{fontSize:13}} aria-hidden/> Effacer</button>
      </div>
    </div>
  );
}

function PhotoUpload({photos,onChange}){
  function handleFiles(e){const files=Array.from(e.target.files);Promise.all(files.map(f=>new Promise(res=>{const r=new FileReader();r.onload=ev=>res(ev.target.result);r.readAsDataURL(f);}))).then(newPhotos=>onChange([...photos,...newPhotos].slice(0,6)));}
  function remove(idx){onChange(photos.filter((_,i)=>i!==idx));}
  return(
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
        {photos.map((p,i)=>(
          <div key={i} style={{position:"relative",borderRadius:"var(--border-radius-md)",overflow:"hidden",aspectRatio:"1",background:"#f1f5f9"}}>
            <img src={p} alt={`photo ${i+1}`} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            <button onClick={()=>remove(i)} style={{position:"absolute",top:4,right:4,width:22,height:22,borderRadius:"50%",background:"rgba(0,0,0,.55)",border:"none",color:"white",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12}}><i className="ti ti-x" aria-hidden/></button>
          </div>
        ))}
        {photos.length<6&&<label style={{borderRadius:"var(--border-radius-md)",border:"1.5px dashed #94a3b8",background:"#f8fafc",aspectRatio:"1",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",gap:4}}>
          <i className="ti ti-camera-plus" style={{fontSize:22,color:"#94a3b8"}} aria-hidden/>
          <span style={{fontSize:11,color:"#94a3b8"}}>Ajouter</span>
          <input type="file" accept="image/*" multiple capture="environment" onChange={handleFiles} style={{display:"none"}}/>
        </label>}
      </div>
      <p style={{fontSize:11,color:"var(--color-text-tertiary)",margin:"6px 0 0"}}>Maximum 6 photos · {photos.length}/6</p>
    </div>
  );
}

function InterventionModal({intervention,user,users,serrures,onSave,onClose,onDelete}){
  const techs=users.filter(u=>u.role==="technicien"&&u.actif!==false);
  const isEdit=!!intervention?.id;
  const prefill=intervention?.prefill;
  const canEditAll=user.role==="commercial"||user.role==="admin";
  const isTech=user.role==="technicien";
  const lockedForTech=isTech&&intervention?.statut==="fait"&&!!intervention?.signature;

  /* ── Initialisation des lignes serrures ── */
  function initLignes(){
    if(intervention?.lignes?.length) return intervention.lignes;
    if(intervention?.serrureId){
      const s=serrures.find(x=>x.id===intervention.serrureId);
      return s?[{serrureId:s.id,nom:s.nom,prixUnit:s.prix,qte:1}]:[];
    }
    if(prefill?.serrureId){
      const s=serrures.find(x=>x.id===prefill.serrureId);
      return s?[{serrureId:s.id,nom:s.nom,prixUnit:s.prix,qte:1}]:[];
    }
    return [];
  }

  const [form,setForm]=useState({
    id:intervention?.id||null,
    clientName:prefill?`${prefill.prenom?prefill.prenom+" ":""}${prefill.nom}`:(intervention?.clientName||""),
    clientPhone:prefill?.telephone||(intervention?.clientPhone||""),
    clientAddress:prefill?.adresse||(intervention?.clientAddress||""),
    date:intervention?.date||D(0),
    heure:intervention?.heure||"09:00",
    technicienId:intervention?.technicienId||techs[0]?.id||null,
    statut:intervention?.statut||"nouveau",
    notes:intervention?.notes||"",
    lignes:initLignes(),
    prixTotal:intervention?.prixTotal||intervention?.prix||"",
    prixModifie:!!intervention?.prixModifie,
    photos:intervention?.photos||[],
    signature:intervention?.signature||null,
  });
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));

  /* ── Calcul automatique du total ── */
  function calcTotal(lignes){
    return lignes.reduce((sum,l)=>{
      const u=parseFloat((l.prixUnit||"0").toString().replace(/\s/g,""))||0;
      return sum+(u*l.qte);
    },0);
  }

  function setLignes(lignes){
    const total=calcTotal(lignes);
    setForm(f=>({...f,lignes,prixTotal:f.prixModifie?f.prixTotal:total.toLocaleString("fr-FR"),prixModifie:f.prixModifie}));
  }

  function addLigne(){
    setLignes([...form.lignes,{serrureId:null,nom:"",prixUnit:"",qte:1}]);
  }

  function removeLigne(idx){
    setLignes(form.lignes.filter((_,i)=>i!==idx));
  }

  function updateLigne(idx,key,val){
    const lignes=form.lignes.map((l,i)=>{
      if(i!==idx) return l;
      if(key==="serrureId"){
        const s=serrures.find(x=>x.id===Number(val));
        return s?{...l,serrureId:s.id,nom:s.nom,prixUnit:s.prix}:{...l,serrureId:null,nom:"",prixUnit:""};
      }
      return {...l,[key]:val};
    });
    setLignes(lignes);
  }

  function handlePrixManuel(val){
    setForm(f=>({...f,prixTotal:val,prixModifie:true}));
  }
  function resetPrix(){
    const total=calcTotal(form.lignes);
    setForm(f=>({...f,prixTotal:total.toLocaleString("fr-FR"),prixModifie:false}));
  }

  /* Résumé pour le technicien */
  const typeResume=form.lignes.length>0
    ?form.lignes.map(l=>`${l.nom||"Serrure"}${l.qte>1?` ×${l.qte}`:""}`).join(", ")
    :(intervention?.type||"");

  const needsProof=isTech&&form.statut==="fait";
  const canSubmit=!needsProof||!!form.signature;

  return(
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box" onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 20px",borderBottom:`1px solid ${T.border}`}}>
          <h3 style={{margin:0,fontSize:16,fontWeight:600,color:T.text}}>{isEdit?"Modifier l'intervention":prefill?"Créer un RDV":"Nouvelle intervention"}</h3>
          <button onClick={onClose} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:18,color:T.textSub,display:"flex",alignItems:"center"}}><i className="ti ti-x" aria-hidden/></button>
        </div>

        <div style={{padding:"14px",display:"flex",flexDirection:"column",gap:13}}>

          {/* ── FORMULAIRE COMMERCIAL/ADMIN ── */}
          {canEditAll&&(
            <>
              <Field label="Nom du client">
                <input value={form.clientName} onChange={e=>set("clientName",e.target.value)} placeholder="Ex: Résidence El Fath" style={{width:"100%"}}/>
              </Field>
              <div className="grid-2">
                <Field label="Téléphone">
                  <input value={form.clientPhone} onChange={e=>set("clientPhone",e.target.value)} placeholder="0600000000" style={{width:"100%"}}/>
                </Field>
                <Field label="Date">
                  <input type="date" value={form.date} onChange={e=>set("date",e.target.value)} style={{width:"100%"}}/>
                </Field>
              </div>
              <Field label="Adresse">
                <input value={form.clientAddress} onChange={e=>set("clientAddress",e.target.value)} placeholder="Adresse du client" style={{width:"100%"}}/>
              </Field>
              <div className="grid-2">
                <Field label="Heure">
                  <input type="time" value={form.heure} onChange={e=>set("heure",e.target.value)} style={{width:"100%"}}/>
                </Field>
                <Field label="Technicien assigné">
                  <select value={form.technicienId||""} onChange={e=>set("technicienId",Number(e.target.value))} style={{width:"100%"}}>
                    {techs.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </Field>
              </div>

              {/* ── LIGNES SERRURES ── */}
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <label style={{fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:".5px"}}>Serrures commandées</label>
                  <button onClick={addLigne} style={{background:T.blueLight,color:T.blue,border:`1px solid ${T.blueMid}`,borderRadius:T.radiusSm,padding:"4px 10px",fontSize:12,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                    <i className="ti ti-plus" style={{fontSize:13}} aria-hidden/> Ajouter une serrure
                  </button>
                </div>

                {form.lignes.length===0?(
                  <div style={{border:`2px dashed ${T.border}`,borderRadius:T.radiusSm,padding:"16px",textAlign:"center",color:T.textHint,fontSize:13}}>
                    <i className="ti ti-lock" style={{fontSize:24,display:"block",marginBottom:6,opacity:.4}} aria-hidden/>
                    Aucune serrure ajoutée
                  </div>
                ):(
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {/* En-tête colonnes */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 80px 76px 30px",gap:8}}>
                      {["Serrure","Prix unit. (MAD)","Qté",""].map(h=>(
                        <span key={h} style={{fontSize:10,fontWeight:600,color:T.textHint,textTransform:"uppercase",letterSpacing:".4px"}}>{h}</span>
                      ))}
                    </div>
                    {form.lignes.map((ligne,idx)=>{
                      const subtotal=(parseFloat((ligne.prixUnit||"0").toString().replace(/\s/g,""))||0)*ligne.qte;
                      return(
                        <div key={idx} style={{background:T.pageBg,borderRadius:T.radiusSm,padding:"10px 12px",border:`1px solid ${T.border}`}}>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 80px 76px 30px",gap:8,alignItems:"center"}}>
                            {/* Sélection serrure */}
                            <select value={ligne.serrureId||""} onChange={e=>updateLigne(idx,"serrureId",e.target.value)} style={{width:"100%",fontSize:13}}>
                              <option value="">— Choisir —</option>
                              {serrures.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}
                            </select>
                            {/* Prix unitaire */}
                            <div style={{position:"relative"}}>
                              <input value={ligne.prixUnit} onChange={e=>updateLigne(idx,"prixUnit",e.target.value)} placeholder="0" inputMode="numeric" style={{width:"100%",paddingRight:28,textAlign:"right",fontSize:13}}/>
                              <span style={{position:"absolute",right:7,top:"50%",transform:"translateY(-50%)",fontSize:10,color:T.textHint,pointerEvents:"none"}}>MAD</span>
                            </div>
                            {/* Quantité */}
                            <div style={{display:"flex",alignItems:"center",border:`1.5px solid ${T.border}`,borderRadius:T.radiusSm,overflow:"hidden",background:"#fff"}}>
                              <button onClick={()=>updateLigne(idx,"qte",Math.max(1,ligne.qte-1))} style={{background:"transparent",border:"none",width:26,height:34,cursor:"pointer",color:T.textSub,fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                              <span style={{flex:1,textAlign:"center",fontSize:13,fontWeight:500,color:T.text}}>{ligne.qte}</span>
                              <button onClick={()=>updateLigne(idx,"qte",ligne.qte+1)} style={{background:"transparent",border:"none",width:26,height:34,cursor:"pointer",color:T.textSub,fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                            </div>
                            {/* Supprimer */}
                            <button onClick={()=>removeLigne(idx)} style={{width:32,height:32,borderRadius:T.radiusSm,background:"#FEF2F2",border:"1px solid #FECACA",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#DC2626"}}>
                              <i className="ti ti-trash" style={{fontSize:13}} aria-hidden/>
                            </button>
                          </div>
                          {/* Sous-total ligne */}
                          {ligne.prixUnit&&ligne.qte>1&&(
                            <p style={{fontSize:11,color:T.textSub,margin:"6px 0 0",textAlign:"right"}}>
                              Sous-total : <strong style={{color:"#7C3AED"}}>{subtotal.toLocaleString("fr-FR")} MAD</strong>
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── TOTAL À PAYER ── */}
              <div style={{background:"#F5F3FF",border:`2px solid #DDD6FE`,borderRadius:T.radius,padding:"16px 18px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div>
                    <p style={{fontSize:12,color:"#7C3AED",margin:0,fontWeight:600,textTransform:"uppercase",letterSpacing:".5px"}}>Montant total à payer</p>
                    {form.prixModifie&&<p style={{fontSize:11,color:"#9CA3AF",margin:"2px 0 0",display:"flex",alignItems:"center",gap:4}}><i className="ti ti-pencil" style={{fontSize:11}} aria-hidden/>Modifié manuellement</p>}
                    {!form.prixModifie&&form.lignes.length>0&&<p style={{fontSize:11,color:"#9CA3AF",margin:"2px 0 0",display:"flex",alignItems:"center",gap:4}}><i className="ti ti-calculator" style={{fontSize:11}} aria-hidden/>Calculé automatiquement</p>}
                  </div>
                  {form.prixModifie&&(
                    <button onClick={resetPrix} style={{fontSize:11,color:"#7C3AED",background:"#EDE9FE",border:"1px solid #DDD6FE",borderRadius:T.radiusSm,padding:"3px 9px",cursor:"pointer",display:"flex",alignItems:"center",gap:3}}>
                      <i className="ti ti-refresh" style={{fontSize:12}} aria-hidden/> Recalculer
                    </button>
                  )}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <input
                    value={form.prixTotal}
                    onChange={e=>handlePrixManuel(e.target.value)}
                    placeholder="0"
                    inputMode="numeric"
                    style={{flex:1,fontSize:22,fontWeight:700,color:"#7C3AED",background:"#EDE9FE",border:"1.5px solid #DDD6FE",borderRadius:T.radiusSm,padding:"10px 14px",textAlign:"right"}}
                  />
                  <span style={{fontSize:16,fontWeight:600,color:"#7C3AED",flexShrink:0}}>MAD</span>
                </div>
                {form.lignes.length>1&&(
                  <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid #DDD6FE"}}>
                    {form.lignes.filter(l=>l.nom).map((l,i)=>{
                      const pu=parseFloat((l.prixUnit||"0").toString().replace(/\s/g,""))||0;
                      return(
                        <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,color:"#7C3AED",marginBottom:3}}>
                          <span style={{opacity:.7}}>{l.nom||"Serrure"} × {l.qte}</span>
                          <span style={{fontWeight:500}}>{(pu*l.qte).toLocaleString("fr-FR")} MAD</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <Field label="Notes">
                <textarea value={form.notes} onChange={e=>set("notes",e.target.value)} placeholder="Informations complémentaires..." rows={2} style={{width:"100%",resize:"vertical"}}/>
              </Field>

              {form.photos?.length>0&&<Field label={`Photos (${form.photos.length})`}><div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>{form.photos.map((p,i)=><div key={i} style={{borderRadius:T.radiusSm,overflow:"hidden",aspectRatio:"1"}}><img src={p} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/></div>)}</div></Field>}
              {form.signature&&<Field label="Signature client"><div style={{border:`1px solid ${T.border}`,borderRadius:T.radiusSm,overflow:"hidden",background:"#f8fafc"}}><img src={form.signature} alt="Signature" style={{width:"100%",maxHeight:100,objectFit:"contain"}}/></div></Field>}
            </>
          )}

          {/* ── VUE TECHNICIEN ── */}
          {isTech&&isEdit&&(
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {lockedForTech&&(
                <div style={{background:"#ECFDF5",border:"1.5px solid #A7F3D0",borderRadius:T.radius,padding:"12px 16px",display:"flex",alignItems:"center",gap:10}}>
                  <i className="ti ti-lock" style={{fontSize:22,color:"#059669",flexShrink:0}} aria-hidden/>
                  <div><p style={{fontSize:13,fontWeight:500,color:"#059669",margin:"0 0 2px"}}>Installation validée et verrouillée</p><p style={{fontSize:12,color:"#059669",margin:0,opacity:.75}}>Contactez un administrateur si nécessaire.</p></div>
                </div>
              )}
              {/* Résumé mission */}
              <div style={{background:T.pageBg,borderRadius:T.radiusSm,padding:"14px 16px",border:`1px solid ${T.border}`}}>
                <p style={{fontSize:14,fontWeight:600,margin:"0 0 8px",color:T.text}}>{form.clientName}</p>
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  <span style={{fontSize:12,color:T.textSub,display:"flex",alignItems:"center",gap:6}}><i className="ti ti-calendar" style={{fontSize:13,color:T.textHint}} aria-hidden/>{new Date(form.date+"T12:00:00").toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})} à {form.heure}</span>
                  {typeResume&&<span style={{fontSize:12,color:T.textSub,display:"flex",alignItems:"center",gap:6}}><i className="ti ti-lock" style={{fontSize:13,color:T.textHint}} aria-hidden/>{typeResume}</span>}
                  <span style={{fontSize:12,color:T.textSub,display:"flex",alignItems:"center",gap:6}}><i className="ti ti-map-pin" style={{fontSize:13,color:T.textHint}} aria-hidden/>{form.clientAddress}</span>
                  {form.clientPhone&&<span style={{fontSize:12,color:T.textSub,display:"flex",alignItems:"center",gap:6}}><i className="ti ti-phone" style={{fontSize:13,color:T.textHint}} aria-hidden/>{form.clientPhone}</span>}
                  {form.notes&&<p style={{fontSize:12,color:T.textHint,margin:"4px 0 0",fontStyle:"italic"}}>{form.notes}</p>}
                </div>
              </div>

              {/* Détail serrures pour le tech */}
              {form.lignes?.length>0&&(
                <div style={{background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:T.radiusSm,padding:"12px 14px"}}>
                  <p style={{fontSize:11,fontWeight:600,color:"#7C3AED",margin:"0 0 8px",textTransform:"uppercase",letterSpacing:".5px"}}>Serrures à installer</p>
                  {form.lignes.map((l,i)=>(
                    <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#7C3AED",marginBottom:4}}>
                      <span>{l.nom||"Serrure"} × {l.qte}</span>
                      <span style={{fontWeight:500}}>{((parseFloat((l.prixUnit||"0").toString().replace(/\s/g,""))||0)*l.qte).toLocaleString("fr-FR")} MAD</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Total à encaisser */}
              {form.prixTotal&&(
                <div style={{background:"#F5F3FF",border:"1.5px solid #DDD6FE",borderRadius:T.radius,padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <p style={{fontSize:12,color:"#7C3AED",margin:"0 0 2px",fontWeight:500}}>Montant total à encaisser</p>
                    <p style={{fontSize:28,fontWeight:700,color:"#7C3AED",margin:0,letterSpacing:"-.5px"}}>{form.prixTotal} <span style={{fontSize:15,fontWeight:500}}>MAD</span></p>
                  </div>
                  <i className="ti ti-cash" style={{fontSize:38,color:"#DDD6FE"}} aria-hidden/>
                </div>
              )}

              {/* Statut */}
              {lockedForTech?(
                <div><p style={{fontSize:11,fontWeight:600,color:T.textSub,margin:"0 0 6px",textTransform:"uppercase",letterSpacing:".5px"}}>Statut</p>
                <div style={{padding:"10px 12px",borderRadius:T.radiusSm,border:"1px solid #A7F3D0",background:"#ECFDF5",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{width:8,height:8,borderRadius:"50%",background:"#22C55E",display:"inline-block"}}/>
                  <span style={{fontSize:13,color:"#059669",fontWeight:500}}>Fait</span>
                  <i className="ti ti-lock" style={{fontSize:13,color:"#059669",marginLeft:"auto",opacity:.6}} aria-hidden/>
                </div></div>
              ):(
                <Field label="Statut de l'intervention">
                  <div className="grid-2">
                    {Object.entries(COLORS).map(([key,c])=>(
                      <div key={key} onClick={()=>set("statut",key)} style={{padding:"10px 12px",borderRadius:T.radiusSm,border:form.statut===key?`2px solid ${c.border}`:`1px solid ${c.border}`,background:form.statut===key?c.bg:"transparent",cursor:"pointer",display:"flex",alignItems:"center",gap:8,transition:"all .12s"}}>
                        <span style={{width:8,height:8,borderRadius:"50%",background:c.dot,flexShrink:0,display:"inline-block"}}/>
                        <span style={{fontSize:13,color:c.text,fontWeight:form.statut===key?500:400}}>{c.label}</span>
                      </div>
                    ))}
                  </div>
                </Field>
              )}

              {form.statut==="fait"&&!lockedForTech&&(
                <>
                  <div style={{borderTop:`1px solid ${T.border}`,paddingTop:12}}>
                    <p style={{fontSize:13,fontWeight:600,color:"#059669",margin:"0 0 10px",display:"flex",alignItems:"center",gap:6}}><i className="ti ti-clipboard-check" style={{fontSize:16}} aria-hidden/> Preuves d'installation</p>
                    <Field label="Photos (optionnel)"><PhotoUpload photos={form.photos} onChange={v=>set("photos",v)}/></Field>
                  </div>
                  <Field label="Signature du client *">
                    <SignaturePad value={form.signature} onChange={v=>set("signature",v)}/>
                    {!form.signature&&<p style={{fontSize:11,color:"#DC2626",margin:"4px 0 0",display:"flex",alignItems:"center",gap:4}}><i className="ti ti-alert-circle" style={{fontSize:12}} aria-hidden/> Signature obligatoire pour valider</p>}
                    {form.signature&&<p style={{fontSize:11,color:"#059669",margin:"4px 0 0",display:"flex",alignItems:"center",gap:4}}><i className="ti ti-circle-check" style={{fontSize:12}} aria-hidden/> Signature enregistrée</p>}
                  </Field>
                </>
              )}
              {lockedForTech&&form.signature&&(
                <Field label="Signature client (enregistrée)">
                  <div style={{border:"1px solid #A7F3D0",borderRadius:T.radiusSm,overflow:"hidden",background:"#f8fafc"}}><img src={form.signature} alt="Signature" style={{width:"100%",maxHeight:110,objectFit:"contain"}}/></div>
                </Field>
              )}
            </div>
          )}

          {/* Statut pour commercial/admin */}
          {canEditAll&&(
            <Field label="Statut">
              <div className="grid-2">
                {Object.entries(COLORS).map(([key,c])=>(
                  <div key={key} onClick={()=>set("statut",key)} style={{padding:"10px 12px",borderRadius:T.radiusSm,border:form.statut===key?`2px solid ${c.border}`:`1px solid ${c.border}`,background:form.statut===key?c.bg:"transparent",cursor:"pointer",display:"flex",alignItems:"center",gap:8,transition:"all .12s"}}>
                    <span style={{width:8,height:8,borderRadius:"50%",background:c.dot,flexShrink:0,display:"inline-block"}}/>
                    <span style={{fontSize:13,color:c.text,fontWeight:form.statut===key?500:400}}>{c.label}</span>
                  </div>
                ))}
              </div>
            </Field>
          )}
        </div>

        {/* Footer */}
        <div style={{display:"flex",gap:8,padding:"14px 16px",borderTop:`1px solid ${T.border}`,justifyContent:"space-between",flexWrap:"wrap"}}>
          <div>{onDelete&&isEdit&&canEditAll&&<button onClick={()=>onDelete(form.id)} style={{background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA",borderRadius:T.radiusSm,padding:"8px 14px",fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}><i className="ti ti-trash" aria-hidden/> Supprimer</button>}</div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={onClose} className="btn-ghost">{lockedForTech?"Fermer":"Annuler"}</button>
            {!lockedForTech&&<button onClick={()=>canSubmit&&onSave({...form,type:form.lignes.map(l=>`${l.nom||"Serrure"}${l.qte>1?` ×${l.qte}`:""}`).join(", ")||"",prix:form.prixTotal})} className="btn-primary" style={{background:canSubmit?T.blue:"#94A3B8",cursor:canSubmit?"pointer":"not-allowed"}}>
              {isEdit?"Enregistrer":"Créer le RDV"}
            </button>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── UTILISATEURS VIEW ─── */
function UtilisateursView({users,onNew,onEdit,onDelete}){
  const roleColors={admin:["#7C3AED","#F5F3FF","#DDD6FE"],commercial:[T.blue,T.blueLight,T.blueMid],technicien:["#059669","#ECFDF5","#A7F3D0"]};
  const roleLabel={admin:"Administrateur",commercial:"Commercial",technicien:"Technicien"};
  const actifs=users.filter(u=>u.actif!==false);
  const inactifs=users.filter(u=>u.actif===false);
  return(
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{marginBottom:4}}>Gestion des utilisateurs</h2>
          <p style={{fontSize:13,color:T.textSub,margin:0}}>{actifs.length} utilisateur{actifs.length>1?"s":""} actif{actifs.length>1?"s":""}</p>
        </div>
        <button className="btn-primary" onClick={onNew}><i className="ti ti-user-plus" style={{fontSize:15}} aria-hidden/> Nouvel utilisateur</button>
      </div>

      {/* Stats rapides */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,marginBottom:"1.5rem"}}>
        {Object.entries(roleLabel).map(([role,label])=>{
          const count=actifs.filter(u=>u.role===role).length;
          const [color,bg]=roleColors[role];
          return(
            <div key={role} className="card" style={{padding:"16px 18px",display:"flex",alignItems:"center",gap:14}}>
              <div style={{width:38,height:38,borderRadius:10,background:bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <i className={`ti ${role==="admin"?"ti-shield-check":role==="commercial"?"ti-briefcase":"ti-tool"}`} style={{fontSize:18,color}} aria-hidden/>
              </div>
              <div>
                <p style={{fontSize:11,color:T.textSub,margin:"0 0 2px",fontWeight:500,textTransform:"uppercase",letterSpacing:".4px"}}>{label}</p>
                <p style={{fontSize:22,fontWeight:700,color:T.text,margin:0}}>{count}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Table utilisateurs */}
      <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}><div className="card" style={{overflow:"hidden",marginBottom:"1.25rem",minWidth:520}}>
        <div style={{padding:"12px 20px",background:T.pageBg,borderBottom:`1px solid ${T.border}`,display:"grid",gridTemplateColumns:"2fr 2fr 1.2fr 1fr 80px",gap:0}}>
          {["Utilisateur","Email","Rôle","Statut",""].map(h=>(
            <span key={h} style={{fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:".5px"}}>{h}</span>
          ))}
        </div>
        {actifs.map((u,idx)=>{
          const [color,bg,border]=roleColors[u.role]||[T.textSub,T.pageBg,T.border];
          const isLast=idx===actifs.length-1;
          return(
            <div key={u.id} style={{display:"grid",gridTemplateColumns:"2fr 2fr 1.2fr 1fr 80px",gap:0,padding:"13px 20px",borderBottom:isLast?"none":`1px solid ${T.border}`,alignItems:"center",transition:"background .12s"}}
              onMouseEnter={e=>e.currentTarget.style.background=T.blueLight}
              onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
              {/* Nom */}
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:36,height:36,borderRadius:"50%",background:`linear-gradient(135deg,${color}cc,${color})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:600,color:"#fff",flexShrink:0}}>{u.avatar}</div>
                <div>
                  <p style={{fontSize:13,fontWeight:600,color:T.text,margin:0}}>{u.name}</p>
                  <p style={{fontSize:11,color:T.textHint,margin:0}}>ID #{u.id}</p>
                </div>
              </div>
              {/* Email */}
              <p style={{fontSize:13,color:T.textSub,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",paddingRight:12}}>{u.lastName||u.name.split(" ").pop()}</p>
              {/* Rôle */}
              <span style={{fontSize:11,background:bg,color,border:`1px solid ${border}`,borderRadius:99,padding:"3px 10px",fontWeight:500,display:"inline-flex",alignItems:"center",gap:5,width:"fit-content"}}>
                <span style={{width:6,height:6,borderRadius:"50%",background:color,display:"inline-block"}}/>
                {roleLabel[u.role]}
              </span>
              {/* Statut */}
              <span style={{fontSize:11,background:"#ECFDF5",color:"#059669",border:"1px solid #A7F3D0",borderRadius:99,padding:"3px 10px",fontWeight:500,display:"inline-flex",alignItems:"center",gap:5,width:"fit-content"}}>
                <span style={{width:6,height:6,borderRadius:"50%",background:"#22C55E",display:"inline-block"}}/>Actif
              </span>
              {/* Actions */}
              <div style={{display:"flex",gap:5,justifyContent:"flex-end"}}>
                <button onClick={()=>onEdit(u)} title="Modifier" style={{width:30,height:30,borderRadius:T.radiusSm,border:`1px solid ${T.border}`,background:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.textSub,transition:"all .12s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=T.blue;e.currentTarget.style.color=T.blue;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.textSub;}}>
                  <i className="ti ti-edit" style={{fontSize:14}} aria-hidden/>
                </button>
              </div>
            </div>
          );
        })}
      </div>
      </div>

      {/* Utilisateurs inactifs */}
      {inactifs.length>0&&(
        <div className="card" style={{overflow:"hidden"}}>
          <div style={{padding:"12px 20px",background:T.pageBg,borderBottom:`1px solid ${T.border}`}}>
            <span style={{fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:".5px"}}>Désactivés ({inactifs.length})</span>
          </div>
          {inactifs.map((u,idx)=>(
            <div key={u.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 20px",borderBottom:idx<inactifs.length-1?`1px solid ${T.border}`:"none",opacity:.55}}>
              <div style={{width:32,height:32,borderRadius:"50%",background:T.pageBg,border:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:600,color:T.textHint}}>{u.avatar}</div>
              <div style={{flex:1}}>
                <p style={{fontSize:13,color:T.textSub,margin:0,textDecoration:"line-through"}}>{u.name}</p>
                <p style={{fontSize:11,color:T.textHint,margin:0}}>{u.lastName||u.name.split(" ").pop()}</p>
              </div>
              <span style={{fontSize:11,background:"#F8FAFC",color:T.textHint,border:`1px solid ${T.border}`,borderRadius:99,padding:"2px 9px"}}>Inactif</span>
              <button onClick={()=>onEdit({...u,actif:true})} style={{fontSize:12,color:T.blue,background:T.blueLight,border:`1px solid ${T.blueMid}`,borderRadius:T.radiusSm,padding:"4px 10px",cursor:"pointer"}}>Réactiver</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── UTILISATEUR MODAL ─── */
function UtilisateurModal({utilisateur,onSave,onClose,onDelete,currentUserId}){
  const isEdit=!!utilisateur?.id;
  const [form,setForm]=useState({
    id:utilisateur?.id||null,
    name:utilisateur?.name||"",
    lastName:utilisateur?.lastName||utilisateur?.name?.split(" ").pop()||"",
    newPassword:"",
    role:utilisateur?.role||"technicien",
    actif:utilisateur?.actif!==false,
  });
  const [showPwd,setShowPwd]=useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const roleOptions=[{value:"admin",label:"Administrateur",icon:"ti-shield-check",desc:"Accès complet à toute la plateforme"},{value:"commercial",label:"Commercial",icon:"ti-briefcase",desc:"CRM, calendrier, interventions"},{value:"technicien",label:"Technicien",icon:"ti-tool",desc:"Planning et statut des interventions"}];

  const pwdRules = form.newPassword ? validatePassword(form.newPassword) : [];
  const pwdStrong = form.newPassword ? isPasswordStrong(form.newPassword) : true; // vide = ok si édition
  const canSave = form.name && form.lastName && (isEdit ? (!form.newPassword || pwdStrong) : pwdStrong);

  return(
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box" onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 20px",borderBottom:`1px solid ${T.border}`}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:38,height:38,borderRadius:10,background:T.blueLight,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <i className="ti ti-user-cog" style={{fontSize:19,color:T.blue}} aria-hidden/>
            </div>
            <h3 style={{margin:0,fontSize:16,fontWeight:600,color:T.text}}>{isEdit?"Modifier l'utilisateur":"Nouvel utilisateur"}</h3>
          </div>
          <button onClick={onClose} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:18,color:T.textSub,display:"flex",alignItems:"center"}}><i className="ti ti-x" aria-hidden/></button>
        </div>

        <div style={{padding:"14px",display:"flex",flexDirection:"column",gap:13}}>

          {/* Info premier login */}
          {!isEdit&&(
            <div style={{background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:T.radiusSm,padding:"10px 14px",fontSize:12,color:"#1E40AF",display:"flex",alignItems:"flex-start",gap:8}}>
              <i className="ti ti-info-circle" style={{fontSize:15,flexShrink:0,marginTop:1}} aria-hidden/>
              <span>L'utilisateur se connectera avec son <strong>nom de famille</strong> et le mot de passe que vous définissez ici. Il pourra le modifier à sa première connexion.</span>
            </div>
          )}

          {/* Nom complet */}
          <Field label="Nom complet *">
            <input value={form.name} onChange={e=>set("name",e.target.value)} placeholder="Ex: Karim Tahiri" style={{width:"100%"}}/>
            <p style={{fontSize:11,color:T.textHint,margin:"4px 0 0"}}>Prénom + Nom</p>
          </Field>

          {/* Nom de famille (identifiant de connexion) */}
          <Field label="Nom de famille — identifiant de connexion *">
            <input value={form.lastName} onChange={e=>set("lastName",e.target.value)} placeholder="Ex: Tahiri" style={{width:"100%",border:`1.5px solid ${T.blue}`,background:T.blueLight}}/>
            <p style={{fontSize:11,color:T.blue,margin:"4px 0 0",display:"flex",alignItems:"center",gap:4}}><i className="ti ti-login" style={{fontSize:12}} aria-hidden/>C'est cet identifiant qui sera utilisé pour se connecter</p>
          </Field>

          {/* Mot de passe */}
          <Field label={isEdit?"Nouveau mot de passe (vide = laisser inchangé, l'utilisateur crée le sien)":"Mot de passe initial *"}>
            <div style={{position:"relative"}}>
              <input value={form.newPassword} onChange={e=>set("newPassword",e.target.value)} placeholder={isEdit?"Laisser vide = premier login obligatoire":"Définir un mot de passe fort"} type={showPwd?"text":"password"} autoComplete="new-password" maxLength={128} style={{width:"100%",paddingRight:40}}/>
              <button onClick={()=>setShowPwd(v=>!v)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"transparent",border:"none",cursor:"pointer",color:T.textHint,display:"flex",alignItems:"center"}}>
                <i className={`ti ${showPwd?"ti-eye-off":"ti-eye"}`} style={{fontSize:16}} aria-hidden/>
              </button>
            </div>
            {form.newPassword&&<PasswordStrengthBar password={form.newPassword}/>}
            {!isEdit&&!form.newPassword&&(
              <p style={{fontSize:11,color:"#D97706",margin:"4px 0 0",display:"flex",alignItems:"center",gap:4}}><i className="ti ti-alert-circle" style={{fontSize:12}} aria-hidden/>Si vide, l'utilisateur créera son mot de passe à la première connexion</p>
            )}
          </Field>

          {/* Rôle */}
          <Field label="Rôle *">
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {roleOptions.map(r=>(
                <div key={r.value} onClick={()=>set("role",r.value)} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:T.radiusSm,border:form.role===r.value?`2px solid ${T.blue}`:`1px solid ${T.border}`,background:form.role===r.value?T.blueLight:"#fff",cursor:"pointer",transition:"all .15s"}}>
                  <div style={{width:36,height:36,borderRadius:8,background:form.role===r.value?T.blueMid:T.pageBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <i className={`ti ${r.icon}`} style={{fontSize:17,color:form.role===r.value?T.blue:T.textSub}} aria-hidden/>
                  </div>
                  <div>
                    <p style={{fontSize:13,fontWeight:500,margin:0,color:form.role===r.value?T.blue:T.text}}>{r.label}</p>
                    <p style={{fontSize:11,color:T.textSub,margin:0}}>{r.desc}</p>
                  </div>
                  {form.role===r.value&&<i className="ti ti-circle-check" style={{fontSize:18,color:T.blue,marginLeft:"auto"}} aria-hidden/>}
                </div>
              ))}
            </div>
          </Field>

          {/* Statut actif/inactif */}
          {isEdit&&utilisateur?.id!==currentUserId&&(
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",borderRadius:T.radiusSm,border:`1px solid ${T.border}`,background:T.pageBg}}>
              <div>
                <p style={{fontSize:13,fontWeight:500,margin:0,color:T.text}}>Compte actif</p>
                <p style={{fontSize:11,color:T.textSub,margin:0}}>Désactiver empêche la connexion sans supprimer les données</p>
              </div>
              <button onClick={()=>set("actif",!form.actif)} style={{width:44,height:24,borderRadius:99,background:form.actif?"#22C55E":"#CBD5E1",border:"none",cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
                <span style={{position:"absolute",top:2,left:form.actif?22:2,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{display:"flex",gap:8,padding:"14px 16px",borderTop:`1px solid ${T.border}`,justifyContent:"space-between",flexWrap:"wrap"}}>
          <div>
            {onDelete&&utilisateur?.id!==currentUserId&&(
              <button onClick={()=>onDelete(form.id)} style={{background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA",borderRadius:T.radiusSm,padding:"8px 14px",fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                <i className="ti ti-user-off" aria-hidden/> Désactiver
              </button>
            )}
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={onClose} className="btn-ghost">Annuler</button>
            <button onClick={()=>canSave&&onSave(form)} className="btn-primary" style={{background:canSave?T.blue:"#94A3B8",cursor:canSave?"pointer":"not-allowed"}}>
              {isEdit?"Enregistrer":"Créer l'utilisateur"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
